<template>
  <NotFoundView
    v-if="showFilmNotFound"
    :heading="filmNotFoundHeading"
    :message="filmNotFoundMessage"
  />
  <main
    v-else
    :class="[
      'single-film-prototype',
      { 'single-film-prototype--no-background': !filmHasBackground },
    ]"
  >
    <section class="hero g-0 position-relative">
      <HeroTopPanel
        :logo-src="assets.logo"
        :background-src="backgroundUrl"
        :mobile-background-src="mobileBackgroundUrl"
        :background-wrap-style="heroBackgroundStyle"
        :login-path="loginPath"
        @open-mobile-search="openMobileSearchOverlay"
      >
        <div class="row g-0 hero__grid align-items-end">
          <div class="col-auto hero__posterWrap">
            <div
              :class="filmPosterFrameClasses"
              data-testid="film-poster-frame"
            >
              <div
                v-if="filmPosterLoading"
                class="image-load-frame__skeleton"
                data-testid="film-poster-skeleton"
                aria-hidden="true"
              ></div>
              <img
                v-if="filmPosterImageSrc"
                :class="filmPosterImageClasses"
                :src="filmPosterImageSrc"
                :alt="`${filmTitle} poster`"
                loading="eager"
                decoding="async"
                fetchpriority="high"
                @load="handleFilmPosterLoad"
                @error="handleFilmPosterError"
              >
            </div>
          </div>
          <div class="col hero__meta">
            <div class="upper-container">
              <div v-if="hasLoadedFilmPayload" class="user-event-container">
                <h1 :class="['item-title', titleLengthClass]" data-testid="film-title">
                  {{ filmTitle }}
                  <span v-if="releaseYear" class="release-year font-darker-gray weight300">{{ releaseYear }}</span>
                </h1>
                <div
                  v-if="releaseYear"
                  class="hero-release-year-tablet release-year font-darker-gray weight300"
                  data-testid="film-hero-release-year-tablet"
                >
                  {{ releaseYear }}
                </div>
                <div class="rating">
                  <div class="rate-box">
                    <div class="rate-text">
                      <p>
                        <span>{{ ratingLabelPrefix }}</span>
                        <span>{{ ratingLabelValue }}</span>
                        <button
                          v-if="isFilmRated"
                          class="rating-reset"
                          data-testid="rating-reset-action"
                          type="button"
                          :disabled="filmRatingPending"
                          aria-label="Remove rating"
                          @click="removeFilmRating"
                        >
                          <svg
                            class="rating-reset__icon"
                            viewBox="0 0 16 16"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2" />
                            <path d="M5.2 5.2 10.8 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                            <path d="M10.8 5.2 5.2 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                          </svg>
                        </button>
                      </p>
                    </div>
                  </div>
                  <div class="rating-stars" @mouseleave="resetRatingPreview">
                    <button
                      v-if="isAuthenticated"
                      v-for="star in 10"
                      :key="`desktop-${star}`"
                      class="rating-star-button"
                      data-testid="rating-star"
                      type="button"
                      :disabled="filmRatingPending"
                      :aria-label="`Rate ${star} out of 10`"
                      @mouseenter="previewRating(star)"
                      @focus="previewRating(star)"
                      @blur="resetRatingPreview"
                      @click="setFilmRating(star)"
                    >
                      <i :class="ratingStarClass(star)"></i>
                    </button>
                    <a
                      v-else
                      v-for="star in 10"
                      :key="`desktop-anon-${star}`"
                      data-testid="rating-star"
                      :href="loginPath"
                      :aria-label="`Rate ${star} out of 10`"
                      @mouseenter="previewRating(star)"
                      @focus="previewRating(star)"
                      @blur="resetRatingPreview"
                    >
                      <i :class="ratingStarClass(star)"></i>
                    </a>
                  </div>
                </div>
                <div class="buttons">
                  <template v-if="isAuthenticated">
                    <button
                      class="btn left-btn"
                      :class="{ 'btn--watched': isFilmWatched }"
                      data-testid="watch-action"
                      type="button"
                      @click="openFilmWatchModal"
                    >
                      <div class="default-btn">
                        <span class="mark-as-watched"><i :class="watchButtonIconClass"></i> {{ watchButtonText }}</span>
                        <span class="set-as-watched"><i :class="watchButtonIconClass"></i>{{ watchButtonShortText }}</span>
                      </div>
                    </button>
                  </template>
                  <template v-else>
                    <a class="btn left-btn" data-testid="watch-action" :href="loginPath">
                      <div class="default-btn">
                        <span class="mark-as-watched"><i class="ion-plus"></i> Mark as watched</span>
                        <span class="set-as-watched"><i class="ion-plus"></i>Set as watched</span>
                      </div>
                    </a>
                  </template>
                  <template v-if="isAuthenticated">
                    <button
                      class="btn"
                      :class="{ 'btn--watchlisted': isFilmWatchlisted }"
                      data-testid="watchlist-action"
                      type="button"
                      :disabled="filmWatchlistPending"
                      @click="toggleFilmWatchlist"
                    >
                      <div class="default-btn">
                        <span class="add-to-watchlist"><i class="ion-bookmark"></i> {{ watchlistButtonText }}</span>
                        <span class="watchlist"><i class="ion-bookmark"></i>{{ watchlistButtonShortText }}</span>
                      </div>
                    </button>
                  </template>
                  <template v-else>
                    <a class="btn" data-testid="watchlist-action" :href="loginPath">
                      <div class="default-btn">
                        <span class="add-to-watchlist"><i class="ion-bookmark"></i> Add to watchlist</span>
                        <span class="watchlist"><i class="ion-bookmark"></i>Watchlist</span>
                      </div>
                    </a>
                  </template>
                  <template v-if="isAuthenticated">
                    <button
                      class="btn right-btn fav-btn"
                      :class="{ 'btn--favourited': isFilmFavourited }"
                      data-testid="favourite-action"
                      type="button"
                      :disabled="filmFavouritePending"
                      @click="toggleFilmFavourite"
                    >
                      <div class="default-btn">
                        <i class="ion-heart"></i> {{ favouriteButtonText }}
                      </div>
                    </button>
                    <button
                      class="btn right-btn fav-btn-sm"
                      :class="{ 'btn--favourited': isFilmFavourited }"
                      type="button"
                      :disabled="filmFavouritePending"
                      @click="toggleFilmFavourite"
                    >
                      <div class="default-btn">
                        <i class="ion-heart"></i>{{ favouriteButtonShortText }}
                      </div>
                    </button>
                  </template>
                  <template v-else>
                    <a class="btn right-btn fav-btn" data-testid="favourite-action" :href="loginPath">
                      <div class="default-btn">
                        <i class="ion-heart"></i> Add to favourites
                      </div>
                    </a>
                    <a class="btn right-btn fav-btn-sm" :href="loginPath">
                      <div class="default-btn">
                        <i class="ion-heart"></i>Favourite
                      </div>
                    </a>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeroTopPanel>
    </section>

    <div v-if="hasLoadedFilmPayload" class="container g-0 hero-tablet-controls">
      <div class="row g-0 hero-tablet-controls__row">
        <div class="col g-0">
          <div class="rating">
            <div class="rate-box">
              <div class="rate-text">
                <p>
                  <span>{{ ratingLabelPrefix }}</span>
                  <span>{{ ratingLabelValue }}</span>
                  <button
                    v-if="isFilmRated"
                    class="rating-reset"
                    data-testid="rating-reset-action"
                    type="button"
                    :disabled="filmRatingPending"
                    aria-label="Remove rating"
                    @click="removeFilmRating"
                  >
                    <svg
                      class="rating-reset__icon"
                      viewBox="0 0 16 16"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2" />
                      <path d="M5.2 5.2 10.8 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                      <path d="M10.8 5.2 5.2 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                    </svg>
                  </button>
                </p>
              </div>
            </div>
            <div class="rating-stars" @mouseleave="resetRatingPreview">
              <button
                v-if="isAuthenticated"
                v-for="star in 10"
                :key="`tablet-${star}`"
                class="rating-star-button"
                type="button"
                :disabled="filmRatingPending"
                :aria-label="`Rate ${star} out of 10`"
                @mouseenter="previewRating(star)"
                @focus="previewRating(star)"
                @blur="resetRatingPreview"
                @click="setFilmRating(star)"
              >
                <i :class="ratingStarClass(star)"></i>
              </button>
              <a
                v-else
                v-for="star in 10"
                :key="`tablet-anon-${star}`"
                :href="loginPath"
                :aria-label="`Rate ${star} out of 10`"
                @mouseenter="previewRating(star)"
                @focus="previewRating(star)"
                @blur="resetRatingPreview"
              >
                <i :class="ratingStarClass(star)"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="row g-0 hero-tablet-controls__row">
        <div class="col g-0">
          <div class="buttons">
            <template v-if="isAuthenticated">
              <button
                class="btn left-btn"
                :class="{ 'btn--watched': isFilmWatched }"
                type="button"
                @click="openFilmWatchModal"
              >
                <div class="default-btn">
                  <span class="mark-as-watched"><i :class="watchButtonIconClass"></i> {{ watchButtonText }}</span>
                  <span class="set-as-watched"><i :class="watchButtonIconClass"></i>{{ watchButtonShortText }}</span>
                </div>
              </button>
            </template>
            <template v-else>
              <a class="btn left-btn" :href="loginPath">
                <div class="default-btn">
                  <span class="mark-as-watched"><i class="ion-plus"></i> Mark as watched</span>
                  <span class="set-as-watched"><i class="ion-plus"></i>Set as watched</span>
                </div>
              </a>
            </template>
            <template v-if="isAuthenticated">
              <button
                class="btn"
                :class="{ 'btn--watchlisted': isFilmWatchlisted }"
                type="button"
                :disabled="filmWatchlistPending"
                @click="toggleFilmWatchlist"
              >
                <div class="default-btn">
                  <span class="add-to-watchlist"><i class="ion-bookmark"></i> {{ watchlistButtonText }}</span>
                  <span class="watchlist"><i class="ion-bookmark"></i>{{ watchlistButtonShortText }}</span>
                </div>
              </button>
            </template>
            <template v-else>
              <a class="btn" :href="loginPath">
                <div class="default-btn">
                  <span class="add-to-watchlist"><i class="ion-bookmark"></i> Add to watchlist</span>
                  <span class="watchlist"><i class="ion-bookmark"></i>Watchlist</span>
                </div>
              </a>
            </template>
            <template v-if="isAuthenticated">
              <button
                class="btn right-btn fav-btn"
                :class="{ 'btn--favourited': isFilmFavourited }"
                type="button"
                :disabled="filmFavouritePending"
                @click="toggleFilmFavourite"
              >
                <div class="default-btn">
                  <i class="ion-heart"></i> {{ favouriteButtonText }}
                </div>
              </button>
              <button
                class="btn right-btn fav-btn-sm"
                :class="{ 'btn--favourited': isFilmFavourited }"
                type="button"
                :disabled="filmFavouritePending"
                @click="toggleFilmFavourite"
              >
                <div class="default-btn">
                  <i class="ion-heart"></i>{{ favouriteButtonShortText }}
                </div>
              </button>
            </template>
            <template v-else>
              <a class="btn right-btn fav-btn" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
              </a>
              <a class="btn right-btn fav-btn-sm" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i>Favourite
                </div>
              </a>
            </template>
          </div>
        </div>
      </div>
      <div v-if="latestFilmWatchText" class="row g-0 hero-tablet-controls__row film-watch-summary-row">
        <div class="col g-0">
          <p class="film-watch-summary" data-testid="film-latest-watch-text">
            <span class="film-watch-summary__label">Last watched:&nbsp;</span>
            <span class="film-watch-summary__detail">{{ latestFilmWatchText }}</span>
          </p>
        </div>
      </div>
    </div>

    <section
      v-if="hasLoadedFilmPayload"
      class="mobile g-0 container"
    >
      <div class="row g-0 item-title-mobile">
        <h1 :class="titleLengthClass">
          {{ filmTitle }}
          <span v-if="releaseYear" class="release-year font-darker-gray weight300">{{ releaseYear }}</span>
        </h1>
      </div>
      <div class="row g-0 votes-row justify-content-between">
        <div class="col g-0 rating-text">
          <p class="rate">
            <span>{{ ratingLabelPrefix }}</span>
            <span>{{ ratingLabelValue }}</span>
            <button
              v-if="isFilmRated"
              class="rating-reset"
              data-testid="rating-reset-action"
              type="button"
              :disabled="filmRatingPending"
              aria-label="Remove rating"
              @click="removeFilmRating"
            >
              <svg
                class="rating-reset__icon"
                viewBox="0 0 16 16"
                aria-hidden="true"
                focusable="false"
              >
                <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2" />
                <path d="M5.2 5.2 10.8 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                <path d="M10.8 5.2 5.2 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
              </svg>
            </button>
          </p>
        </div>
      </div>
      <div class="row mobile-rating">
        <div class="col rating-stars justify-content-center" @mouseleave="resetRatingPreview">
          <button
            v-if="isAuthenticated"
            v-for="star in 10"
            :key="`mobile-${star}`"
            class="rating-star-button"
            type="button"
            :disabled="filmRatingPending"
            :aria-label="`Rate ${star} out of 10`"
            @mouseenter="previewRating(star)"
            @focus="previewRating(star)"
            @blur="resetRatingPreview"
            @click="setFilmRating(star)"
          >
            <i :class="ratingStarClass(star)"></i>
          </button>
          <a
            v-else
            v-for="star in 10"
            :key="`mobile-anon-${star}`"
            :href="loginPath"
            :aria-label="`Rate ${star} out of 10`"
            @mouseenter="previewRating(star)"
            @focus="previewRating(star)"
            @blur="resetRatingPreview"
          >
            <i :class="ratingStarClass(star)"></i>
          </a>
        </div>
      </div>
      <div class="row g-0">
        <div class="col g-0">
          <div class="buttons">
            <template v-if="isAuthenticated">
              <button
                class="btn left-btn"
                :class="{ 'btn--watched': isFilmWatched }"
                type="button"
                @click="openFilmWatchModal"
              >
                <div class="default-btn">
                  <span class="mark-as-watched"><i :class="watchButtonIconClass"></i> {{ watchButtonText }}</span>
                  <span class="set-as-watched"><i :class="watchButtonIconClass"></i>{{ watchButtonShortText }}</span>
                </div>
              </button>
            </template>
            <template v-else>
              <a class="btn left-btn" :href="loginPath">
                <div class="default-btn">
                  <span class="mark-as-watched"><i class="ion-plus"></i> Mark as watched</span>
                  <span class="set-as-watched"><i class="ion-plus"></i>Set as watched</span>
                </div>
              </a>
            </template>
              <template v-if="isAuthenticated">
                <button
                  class="btn"
                  :class="{ 'btn--watchlisted': isFilmWatchlisted }"
                  type="button"
                  :disabled="filmWatchlistPending"
                  @click="toggleFilmWatchlist"
                >
                  <div class="default-btn">
                    <span class="add-to-watchlist"><i class="ion-bookmark"></i> {{ watchlistButtonText }}</span>
                    <span class="watchlist"><i class="ion-bookmark"></i>{{ watchlistButtonShortText }}</span>
                  </div>
                </button>
              </template>
              <template v-else>
                <a class="btn" :href="loginPath">
                  <div class="default-btn">
                    <span class="add-to-watchlist"><i class="ion-bookmark"></i> Add to watchlist</span>
                    <span class="watchlist"><i class="ion-bookmark"></i>Watchlist</span>
                  </div>
                </a>
              </template>
            <template v-if="isAuthenticated">
              <button
                class="btn right-btn fav-btn"
                :class="{ 'btn--favourited': isFilmFavourited }"
                type="button"
                :disabled="filmFavouritePending"
                @click="toggleFilmFavourite"
              >
                <div class="default-btn">
                  <i class="ion-heart"></i> {{ favouriteButtonText }}
                </div>
              </button>
              <button
                class="btn right-btn fav-btn-sm"
                :class="{ 'btn--favourited': isFilmFavourited }"
                type="button"
                :disabled="filmFavouritePending"
                @click="toggleFilmFavourite"
              >
                <div class="default-btn">
                  <i class="ion-heart"></i>{{ favouriteButtonShortText }}
                </div>
              </button>
            </template>
            <template v-else>
              <a class="btn right-btn fav-btn" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
              </a>
              <a class="btn right-btn fav-btn-sm" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i>Favourite
                </div>
              </a>
            </template>
          </div>
        </div>
      </div>
      <div v-if="latestFilmWatchText" class="row g-0 film-watch-summary-row film-watch-summary-row--mobile">
        <div class="col g-0">
          <p class="film-watch-summary" data-testid="film-latest-watch-text">
            <span class="film-watch-summary__label">Last watched:&nbsp;</span>
            <span class="film-watch-summary__detail">{{ latestFilmWatchText }}</span>
          </p>
        </div>
      </div>
    </section>
    <section
      v-else
      class="mobile g-0 container mobile-hero-placeholder"
      data-testid="film-mobile-hero-placeholder"
      aria-hidden="true"
    ></section>

    <a class="top-link" id="top"></a>
    <div class="container g-0 mobile-nav">
      <div class="row">
        <div class="col">
          <nav class="nav">
            <ul class="film-nav" data-testid="film-tabs">
              <li>
                <RouterLink :class="{ active: activeSection === 'overview' }" :to="basePath">Overview</RouterLink>
              </li>
              <li>
                <RouterLink
                  :class="['cast-and-crew', { active: activeSection === 'credits' }]"
                  :to="`${basePath}/credits`"
                >Cast &amp; Crew</RouterLink>
                <RouterLink
                  :class="['credits', { active: activeSection === 'credits' }]"
                  :to="`${basePath}/credits`"
                >Credits</RouterLink>
              </li>
              <li>
                <RouterLink :class="{ active: activeSection === 'lists' }" :to="`${basePath}/lists`">Lists</RouterLink>
              </li>
              <li>
                <RouterLink
                  :class="{ active: activeSection === 'comments' }"
                  :to="`${basePath}/comments`"
                >Comments</RouterLink>
              </li>
              <li v-if="isAuthenticated">
                <RouterLink :class="{ active: activeSection === 'you' }" :to="`${basePath}/you`">You</RouterLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <div v-if="activeSection === 'overview'" class="container g-0" data-testid="overview-panel">
      <div v-if="latestFilmWatchText" class="row g-0 film-watch-summary-row film-watch-summary-row--overview">
        <div class="col-12 col-sm-12 col-md-8 col-lg-7">
          <p class="film-watch-summary" data-testid="film-latest-watch-text">
            <span class="film-watch-summary__label">Last watched:&nbsp;</span>
            <span class="film-watch-summary__detail">{{ latestFilmWatchText }}</span>
          </p>
        </div>
      </div>
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-8 col-lg-7">
          <div class="film-data film__main">
            <div class="overview">
              <p data-testid="film-overview-text">
                <span data-testid="film-overview-body">{{ displayedFilmOverview }}</span>
                <button
                  v-if="hasExpandableFilmOverview"
                  class="overview-toggle"
                  data-testid="film-overview-toggle"
                  type="button"
                  @click="toggleFilmOverview"
                >
                  <span class="overview-toggle__icon" aria-hidden="true">{{ filmOverviewExpanded ? '▲' : '▼' }}</span>
                </button>
              </p>
            </div>
            <div class="middle-section-mobile g-0 container" data-testid="mobile-specs">
              <FilmSpecs />
            </div>
            <div class="film-data-section main-cast">
              <div class="title-section">
                <div>
                  <p>Main cast</p>
                </div>
                <div><a class="more-link" :href="`${basePath}/credits`">Full cast &amp; crew &gt;</a></div>
              </div>
              <div class="cast-list">
                <p v-if="filmLoaded && mainCast.length === 0" class="no-cast-message" data-testid="no-cast-message">
                  No cast available.
                </p>
                <div
                  v-for="(castRow, rowIndex) in mainCastRows"
                  :key="`cast-row-${rowIndex}`"
                  class="cast-row"
                >
                  <CastMember
                    v-for="cast in castRow"
                    :key="`${cast.actor}-${cast.role}-${cast.url}`"
                    :image="cast.image"
                    :actor="cast.actor"
                    :role="cast.role"
                    :url="cast.url"
                  />
                </div>
              </div>
            </div>
            <div class="film-data-section lists">
              <div class="title-section">
                <div>
                  <p>Lists with this film</p>
                </div>
                <div>
                  <a class="more-link" :href="`${basePath}/lists`">
                    More lists <span class="with-this-film">with this film </span>&gt;
                  </a>
                </div>
              </div>
              <TransitionGroup
                v-if="overviewLists.length > 0"
                name="overview-list"
                tag="div"
                class="film-list-stack"
              >
                <div
                  v-for="(filmList, filmListIndex) in overviewLists"
                  :key="filmList.id"
                  class="film-list"
                  data-testid="overview-film-list"
                >
                  <button
                    v-if="filmListIndex === 0 && filmListSuccessTitle"
                    class="film-list-success-notice"
                    :class="{ 'film-list-success-notice--dismissing': filmListSuccessDismissing }"
                    data-testid="film-list-success-notice"
                    type="button"
                    @click="dismissFilmListSuccessNotice"
                  >
                    This film was added to list "{{ filmListSuccessTitle }}".
                  </button>
                  <div class="list-covers">
                    <div
                      v-for="item in filmList.items"
                      :key="item.id"
                      class="list-cover"
                    >
                      <a :href="filmListPath(filmList)">
                        <img :src="item.posterUrl" :alt="`${item.title} poster`">
                        <span
                          v-if="item.showPlaceholderLabel"
                          class="film-list-placeholder-title"
                          data-testid="film-list-placeholder-title"
                        >
                          {{ item.placeholderLabel }}
                        </span>
                      </a>
                    </div>
                  </div>
                  <p><a class="list-title" :href="filmListPath(filmList)">{{ filmList.title }}</a></p>
                  <CuratedListLabel v-if="filmList.is_curated" />
                  <p v-else-if="filmList.official" class="list-owner official-list">Official list</p>
                  <p v-else class="list-owner">by <a :href="filmList.ownerHref">{{ filmList.ownerName }}</a></p>
                </div>
              </TransitionGroup>
              <p
                v-else-if="filmLoaded"
                class="film-lists-empty"
                data-testid="overview-film-lists-empty"
              >
                This film doesn't belong any list, yet.
              </p>
              <div class="add-to-list">
                <div>
                  <template v-if="isAuthenticated">
                    <button
                      class="btn-sm"
                      data-testid="film-list-open"
                      type="button"
                      @click="openFilmListModal"
                    >
                      + Add to list
                    </button>
                  </template>
                  <template v-else>
                    <a class="btn-sm" data-testid="film-list-open" :href="loginPath">+ Add to list</a>
                  </template>
                </div>
              </div>
            </div>
            <div class="film-data-section discussion">
              <div class="title-section">
                <div>
                  <p>Latest comments</p>
                </div>
                <div><a class="more-link" :href="`${basePath}/comments`">View comments &gt;</a></div>
              </div>
            </div>
            <div class="container g-0 comments">
              <div class="row g-0">
                <div class="col g-0">
                  <template v-if="latestOverviewComments.length > 0">
                    <div
                      v-for="comment in latestOverviewComments"
                      :key="comment.id"
                      class="comment-container"
                      data-testid="comment-card"
                    >
                      <div class="comment-author">
                        <a class="comment-owner" :href="comment.userHref">{{ comment.username }}</a> wrote:
                      </div>
                      <div class="comment-date">{{ overviewCommentTimeText(comment) }}</div>
                      <div class="comment-body">
                        <blockquote>
                          {{ overviewCommentPreviewText(comment) }}
                        </blockquote>
                        <a
                          v-if="overviewCommentIsTruncated(comment)"
                          class="more-link comment-preview-link"
                          :href="`${basePath}/comments`"
                        >
                          View full comment
                        </a>
                      </div>
                    </div>
                  </template>
                  <p
                    v-else-if="latestOverviewCommentsLoaded"
                    class="comment-empty-copy"
                    data-testid="latest-comments-empty"
                  >
                    No comments, yet.
                    <a class="more-link" :href="`${basePath}/comments`">Be the first to comment.</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-none d-sm-block col-lg-3 col-md-4 middle-section">
          <div class="film-specs film__middle">
            <FilmSpecsDesktop />
          </div>
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
          <template v-if="showFilmAds">
          </template>
        </div>
      </div>
    </div>
    <div v-else-if="activeSection === 'lists'" class="container g-0" data-testid="lists-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <div class="container g-0 film-lists-panel">
            <div class="row g-0 credits-title">
              <div class="col g-0">
                <h2 class="sub-heading" data-testid="film-lists-heading">{{ filmListsHeading }}</h2>
              </div>
            </div>

            <div class="row g-0 list-addition-row">
              <div class="col g-0">
                <template v-if="isAuthenticated">
                  <button
                    class="btn-sm list-addition-btn"
                    data-testid="film-list-open"
                    type="button"
                    @click="openFilmListModal"
                  >
                    + Add to list
                  </button>
                </template>
                <template v-else>
                  <a class="btn-sm list-addition-btn" data-testid="film-list-open" :href="loginPath">+ Add to list</a>
                </template>
              </div>
            </div>

            <p v-if="filmListsPageError" class="film-lists-empty" data-testid="film-lists-error">
              {{ filmListsPageError }}
            </p>
            <p v-else-if="filmListsPageLoading" class="film-lists-empty" data-testid="film-lists-loading">
              Loading lists...
            </p>
            <p
              v-else-if="filmListsPageLoaded && filmLists.length === 0"
              class="film-lists-empty"
              data-testid="film-lists-empty"
            >
              This film doesn't belong any list, yet.
            </p>

            <TransitionGroup
              v-else-if="filmLists.length > 0"
              name="overview-list"
              tag="div"
              class="film-list-stack film-list-stack--page"
            >
              <div
                v-for="(filmList, filmListIndex) in filmLists"
                :key="filmList.id"
                class="film-list film-list--page"
                data-testid="film-list-page-entry"
              >
                <button
                  v-if="filmListIndex === 0 && filmListSuccessTitle"
                  class="film-list-success-notice"
                  :class="{ 'film-list-success-notice--dismissing': filmListSuccessDismissing }"
                  data-testid="film-list-success-notice"
                  type="button"
                  @click="dismissFilmListSuccessNotice"
                >
                  This film was added to list "{{ filmListSuccessTitle }}".
                </button>
                <div class="list-posters">
                  <div
                    v-for="item in filmList.items"
                    :key="item.id"
                    class="list-poster"
                  >
                    <a :href="filmListPath(filmList)">
                      <img :src="item.posterUrl" :alt="`${item.title} poster`">
                      <span
                        v-if="item.showPlaceholderLabel"
                        class="film-list-placeholder-title"
                        data-testid="film-list-placeholder-title"
                      >
                        {{ item.placeholderLabel }}
                      </span>
                    </a>
                  </div>
                </div>
                <a class="list-title" :href="filmListPath(filmList)">{{ filmList.title }}</a>
                <CuratedListLabel v-if="filmList.is_curated" />
                  <p v-else-if="filmList.official" class="list-owner official-list">Official list</p>
                <p v-else class="list-owner">by <a :href="filmList.ownerHref">{{ filmList.ownerName }}</a></p>
              </div>

              <nav
                v-if="showFilmListsPagination"
                key="film-lists-pagination"
                class="film-detail-lists-pagination film-detail-lists-pagination--with-mobile"
                aria-label="Film lists pagination"
              >
                <ul class="pagination mb-0">
                  <li class="page-item" :class="{ disabled: !canGoToPreviousFilmListsPage || filmListsPageLoading }">
                    <button
                      class="page-link"
                      data-testid="film-lists-prev"
                      type="button"
                      @click="goToFilmListsPage(filmListsPageNumber - 1)"
                    >
                      Previous
                    </button>
                  </li>
                  <li
                    v-for="pageItem in filmListsPages"
                    :key="`film-lists-${pageItem.key}`"
                    class="page-item"
                    :class="{ active: pageItem.page === filmListsPageNumber, disabled: pageItem.ellipsis }"
                  >
                    <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">
                      {{ pageItem.label }}
                    </span>
                    <button
                      v-else
                      class="page-link"
                      :data-testid="`film-lists-page-${pageItem.page}`"
                      type="button"
                      :aria-label="`Go to page ${pageItem.page}`"
                      @click="goToFilmListsPage(pageItem.page)"
                    >
                      {{ pageItem.label }}
                    </button>
                  </li>
                  <li class="page-item" :class="{ disabled: !canGoToNextFilmListsPage || filmListsPageLoading }">
                    <button
                      class="page-link"
                      data-testid="film-lists-next"
                      type="button"
                      @click="goToFilmListsPage(filmListsPageNumber + 1)"
                    >
                      Next
                    </button>
                  </li>
                </ul>
                <CompactMobilePagination
                  :current-page="filmListsPageNumber"
                  :total-pages="filmListsNumPages"
                  :loading="filmListsPageLoading"
                  test-id-prefix="film-lists"
                  @page-change="goToFilmListsPage"
                />
              </nav>
            </TransitionGroup>
          </div>
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
          <template v-if="showFilmAds">
          </template>
        </div>
      </div>
    </div>
    <div v-else-if="activeSection === 'comments'" class="container g-0" data-testid="comments-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <CommentsPanel
            :base-path="basePath"
            heading="Discussion"
            :is-active="activeSection === 'comments'"
            target-type="film"
            :target-id="film.id"
            test-id-prefix="film"
          />
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
          <template v-if="showFilmAds">
          </template>
        </div>
      </div>
    </div>
    <div v-else-if="activeSection === 'you'" class="container g-0" data-testid="you-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-8 col-lg-12" data-testid="film-you-content">
          <div class="container g-0">
            <div class="row g-0 credits-title">
              <div class="col g-0">
                <h2 class="sub-heading" data-testid="film-you-heading">{{ filmYouHeading }}</h2>
              </div>
            </div>
            <p v-if="filmYouWatchesError" class="film-you-empty" data-testid="film-you-error">
              {{ filmYouWatchesError }}
            </p>
            <p v-else-if="filmYouWatchesLoading" class="film-you-empty" data-testid="film-you-loading">
              Loading watches...
            </p>
            <p
              v-else-if="filmYouWatchesLoaded && filmYouWatches.length === 0"
              class="film-you-empty"
              data-testid="film-you-empty"
            >
              No watches yet.
            </p>
            <div v-else class="row g-0" data-testid="film-you-watch-list-row">
              <div class="film-you-watch-list col-12 col-lg-7" data-testid="film-you-watch-list">
                <div
                  v-for="(watch, watchIndex) in filmYouWatches"
                  :key="watch.id"
                  :class="[
                    'film-you-watch-row',
                    watchIndex < filmYouWatches.length - 1 || filmYouWatches.length === 1
                      ? 'film-you-watch-row--bordered'
                      : null,
                  ]"
                  data-testid="film-you-watch-row"
                >
                  <div class="film-you-watch-date-column">
                    <p class="film-you-watch-date" data-testid="film-you-watch-date">
                      {{ filmYouWatchDateText(watch) }}
                    </p>
                  </div>
                  <div class="film-you-watch-format" data-testid="film-you-watch-format">
                    {{ filmYouWatchFormatText(watch) }}
                  </div>
                  <div class="film-you-watch-actions">
                    <button
                      class="film-you-watch-action"
                      data-testid="film-you-watch-edit"
                      type="button"
                      aria-label="Edit watch"
                      @click="openFilmWatchEditModal(watch)"
                    >
                      <i class="ion-edit" aria-hidden="true"></i>
                    </button>
                    <button
                      class="film-you-watch-action"
                      data-testid="film-you-watch-remove"
                      type="button"
                      aria-label="Remove watch"
                      @click="removeFilmYouWatch(watch)"
                    >
                      <i class="ion-ios-close-outline" aria-hidden="true"></i>
                    </button>
                  </div>
                  <p
                    v-if="watch.description"
                    class="film-you-watch-description"
                    data-testid="film-you-watch-description"
                  >
                    "{{ watch.description }}"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="activeSection === 'credits'" class="container g-0" data-testid="credits-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <div class="container g-0">
            <div class="row g-0 credits-title">
              <div class="col g-0">
                <h2 class="sub-heading">Cast &amp; Crew</h2>
              </div>
            </div>
            <div v-if="creditsAvailable" class="row g-0">
              <div class="col g-0">
                <ul class="credits-buttons d-flex list-unstyled">
                  <li v-if="showCreditsCastSection">
                    <a class="cast-btn" href="#cast" @click.prevent="scrollCreditsToAnchor">Cast</a>
                  </li>
                  <li v-if="filteredCreditsDirectors.length > 0">
                    <a href="#directing" @click.prevent="scrollCreditsToAnchor">Directing</a>
                  </li>
                  <li v-if="filteredCreditsWriters.length > 0">
                    <a href="#writing" @click.prevent="scrollCreditsToAnchor">Writing</a>
                  </li>
                  <li v-if="filteredCreditsCrew.length > 0">
                    <a href="#crew" @click.prevent="scrollCreditsToAnchor">Crew</a>
                  </li>
                </ul>
              </div>
            </div>
            <div v-if="creditsAvailable" class="row g-0 credits-filter-container">
              <div class="col g-0">
                <p>Filter cast &amp; crew by name</p>
                <div class="credits-filter-field">
                  <input
                    id="credits-filter"
                    v-model="creditsFilter"
                    class="credits-filter"
                    type="search"
                    placeholder="Type cast name or crew member name"
                  >
                </div>
              </div>
            </div>
            <a v-if="showCreditsCastSection" id="cast"></a>
            <div v-if="showCreditsCastSection" class="row g-0 credits-title-section">
              <div class="col g-0">
                <p data-testid="credits-cast-title">{{ creditsCastTitle }}</p>
              </div>
            </div>
            <div v-if="showCreditsCastSection" class="row g-0">
              <div class="col g-0">
                <p
                  v-if="filmLoaded && creditsCast.length === 0 && !creditsFilterTerm"
                  class="no-cast-message"
                  data-testid="credits-no-cast-message"
                >
                  No cast available.
                </p>
                <div
                  v-for="member in filteredCreditsCast"
                  :key="`${member.name}-${member.role}-${member.url}`"
                  class="cast-member-row"
                  data-testid="credits-cast-member"
                >
                  <div class="cast-member-photo">
                    <img :src="member.image" alt="">
                  </div>
                  <div class="names">
                    <div class="cast-member-name">
                      <a :href="member.url">{{ member.name }}</a>
                    </div>
                    <div v-if="member.role" class="cast-character-name">
                      <p>{{ member.role }}</p>
                    </div>
                  </div>
                </div>
                <div class="back-to-top">
                  <a href="#top" @click.prevent="scrollCreditsToAnchor">
                    Back to top <i class="ion-android-arrow-up"></i>
                  </a>
                </div>
              </div>
            </div>
            <template v-if="creditsAvailable">
              <template v-for="section in creditsSections" :key="section.id">
                <a :id="section.id"></a>
                <div class="row g-0 credits-title-section">
                  <div class="col g-0">
                    <p :data-testid="section.titleTestId || null">{{ section.title }}</p>
                  </div>
                </div>
                <div class="row g-0">
                  <div class="col g-0">
                    <div
                      v-for="member in section.members"
                      :key="`${section.id}-${member.name}-${member.role}`"
                      class="cast-member-row"
                      :data-testid="section.memberTestId || null"
                    >
                      <div class="cast-member-photo">
                        <img :src="member.image" alt="">
                      </div>
                      <div class="names">
                        <div class="cast-member-name">
                          <a :href="member.url || '#'">{{ member.name }}</a>
                        </div>
                        <div v-if="member.role" class="cast-character-name">
                          <p>{{ member.role }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="back-to-top">
                  <a href="#top" @click.prevent="scrollCreditsToAnchor">
                    Back to top <i class="ion-android-arrow-up"></i>
                  </a>
                </div>
              </template>
            </template>
            <div v-else-if="filmLoaded" class="row g-0 credits-empty-row">
              <div class="col g-0">
                <p class="no-cast-message no-credits-message" data-testid="credits-empty-message">
                  No credits available
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
          <template v-if="showFilmAds">
          </template>
        </div>
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="filmListModalOpen"
        class="film-watch-modal-overlay"
        data-testid="film-list-modal-overlay"
      >
        <button
          class="film-watch-modal-backdrop"
          type="button"
          aria-label="Close add to list dialog"
          :disabled="filmListModalPending"
          @click="closeFilmListModal"
        ></button>
        <div
          class="film-watch-modal film-list-modal"
          data-testid="film-list-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="film-list-modal-title"
          @click.stop
        >
          <div class="film-watch-modal__header">
            <h2 id="film-list-modal-title" class="film-watch-modal__title film-list-modal__title">
              Add "{{ filmTitle }}"<br>to list:
            </h2>
            <button
              class="film-watch-modal__close"
              data-testid="film-list-close"
              type="button"
              aria-label="Close add to list dialog"
              :disabled="filmListModalPending"
              @click="closeFilmListModal"
            >
              <span
                class="film-watch-modal__close-icon ion-close-round"
                aria-hidden="true"
              ></span>
            </button>
          </div>

          <p
            v-if="filmListModalError"
            class="film-watch-modal__error"
            data-testid="film-list-error"
          >
            {{ filmListModalError }}
          </p>

          <form
            v-if="!filmListCreateMode"
            class="film-watch-modal__form"
            @submit.prevent="submitFilmListAddition"
          >
            <section class="film-watch-modal__section">
              <div class="film-watch-modal__field">
                <span>List</span>
                <div class="film-list-picker" @click.stop @mousedown.stop>
                  <button
                    class="film-list-picker__toggle"
                    :class="{ 'film-list-picker__toggle--open': filmListPickerOpen }"
                    data-testid="film-list-select"
                    type="button"
                    :disabled="filmListModalPending || filmListModalLoading"
                    @click.stop="filmListPickerOpen = !filmListPickerOpen"
                  >
                    <span class="film-list-picker__label">{{ selectedFilmListLabel }}</span>
                    <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                  </button>
                  <div
                    v-if="filmListPickerOpen"
                    class="film-list-picker__menu"
                    data-testid="film-list-picker-menu"
                  >
                    <button
                      class="film-list-picker__option"
                      :class="{ 'film-list-picker__option--selected': !filmListSelectedListId }"
                      data-testid="film-list-option-empty"
                      type="button"
                      @click.stop="selectFilmList('')"
                    >
                      Choose list...
                    </button>
                    <button
                      v-for="filmList in filmListOptions"
                      :key="filmList.id"
                      class="film-list-picker__option"
                      :class="{ 'film-list-picker__option--selected': String(filmList.id) === filmListSelectedListId }"
                      :data-testid="`film-list-option-${filmList.id}`"
                      type="button"
                      @click.stop="selectFilmList(String(filmList.id))"
                    >
                      {{ filmList.title }}
                    </button>
                  </div>
                </div>
                <select
                  v-model="filmListSelectedListId"
                  :disabled="filmListModalPending || filmListModalLoading"
                  class="film-list-picker__native"
                  tabindex="-1"
                  aria-hidden="true"
                >
                  <option value="">Choose list...</option>
                  <option
                    v-for="filmList in filmListOptions"
                    :key="filmList.id"
                    :value="String(filmList.id)"
                  >
                    {{ filmList.title }}
                  </option>
                </select>
              </div>
            </section>

            <div class="film-watch-modal__actions film-list-modal__actions">
              <button
                class="film-watch-modal__action film-watch-modal__action--secondary"
                data-testid="film-list-create-toggle"
                type="button"
                :disabled="filmListModalPending || filmListModalLoading"
                @click="openFilmListCreateMode"
              >
                Create new list
              </button>
              <div class="film-watch-modal__action-group">
                <button
                  class="film-watch-modal__action film-watch-modal__action--secondary"
                  data-testid="film-list-cancel"
                  type="button"
                  :disabled="filmListModalPending"
                  @click="closeFilmListModal"
                >
                  Cancel
                </button>
                <button
                  class="film-watch-modal__action"
                  data-testid="film-list-add-submit"
                  type="submit"
                  :disabled="filmListModalPending || filmListModalLoading || !filmListSelectedListId"
                >
                  Add to list
                </button>
              </div>
            </div>
          </form>

          <form
            v-else
            class="film-watch-modal__form"
            @submit.prevent="submitFilmListCreate"
          >
            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Create a new list</h3>
              </div>
              <label class="film-watch-modal__field">
                <span>Title</span>
                <input
                  v-model="filmListCreateTitle"
                  class="film-list-modal__input"
                  data-testid="film-list-create-title"
                  type="text"
                  maxlength="255"
                  :disabled="filmListModalPending"
                >
              </label>
              <label class="film-watch-modal__field">
                <span>Description</span>
                <textarea
                  v-model="filmListCreateDescription"
                  class="film-watch-modal__textarea"
                  data-testid="film-list-create-description"
                  rows="5"
                  :disabled="filmListModalPending"
                ></textarea>
              </label>
              <label class="film-watch-modal__checkbox">
                <input
                  v-model="filmListCreatePublic"
                  data-testid="film-list-create-public"
                  type="checkbox"
                  :disabled="filmListModalPending"
                >
                <span>Public</span>
              </label>
            </section>

            <div class="film-watch-modal__actions">
              <div class="film-watch-modal__action-group">
                <button
                  class="film-watch-modal__action film-watch-modal__action--secondary"
                  data-testid="film-list-create-cancel"
                  type="button"
                  :disabled="filmListModalPending"
                  @click="closeFilmListModal"
                >
                  Cancel
                </button>
                <button
                  class="film-watch-modal__action"
                  data-testid="film-list-create-submit"
                  type="submit"
                  :disabled="filmListModalPending"
                >
                  Create
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
    <Teleport to="body">
      <div
        v-if="filmWatchModalOpen"
        class="film-watch-modal-overlay"
        data-testid="film-watch-modal-overlay"
      >
        <button
          class="film-watch-modal-backdrop"
          type="button"
          aria-label="Close watch modal"
          :disabled="filmWatchModalPending"
          @click="closeFilmWatchModal"
        ></button>
        <div
          class="film-watch-modal"
          data-testid="film-watch-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="film-watch-modal-title"
        >
          <div class="film-watch-modal__header">
            <h2 id="film-watch-modal-title" class="film-watch-modal__title">
              {{ filmWatchModalTitle }}
            </h2>
            <button
              class="film-watch-modal__close"
              data-testid="film-watch-close"
              type="button"
              aria-label="Close watch modal"
              :disabled="filmWatchModalPending"
              @click="closeFilmWatchModal"
            >
              <svg
                class="film-watch-modal__close-icon"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <circle cx="10" cy="10" r="8.5" fill="none" stroke="currentColor" stroke-width="1.4" />
                <path
                  d="M7 7 L13 13 M13 7 L7 13"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>

          <p
            v-if="filmWatchHasExisting && !filmWatchEditing"
            class="film-watch-modal__note"
            data-testid="film-watch-existing-note"
          >
            You already have {{ filmWatchExistingLabel }} for this film. Saving will add a new watch.
          </p>

          <p
            v-if="filmWatchModalError"
            class="film-watch-modal__error"
            data-testid="film-watch-error"
          >
            {{ filmWatchModalError }}
          </p>

          <form class="film-watch-modal__form" @submit.prevent="saveFilmWatch">
            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Watch date</h3>
                <p>Optional</p>
              </div>
              <div class="film-watch-modal__date-grid">
                <label
                  class="film-watch-modal__field"
                  :class="{ 'film-watch-modal__field--picker-open': filmWatchDatePickerOpen === 'year' }"
                >
                  <span>Year</span>
                  <div
                    class="film-list-picker"
                    :class="{ 'film-list-picker--open': filmWatchDatePickerOpen === 'year' }"
                  >
                    <button
                      class="film-list-picker__toggle"
                      :class="{ 'film-list-picker__toggle--open': filmWatchDatePickerOpen === 'year' }"
                      data-testid="film-watch-year-picker"
                      type="button"
                      :disabled="filmWatchModalPending"
                      @click="toggleFilmWatchDatePicker('year')"
                    >
                      <span class="film-list-picker__label">{{ selectedFilmWatchYearLabel }}</span>
                      <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                    </button>
                    <div
                      v-if="filmWatchDatePickerOpen === 'year'"
                      class="film-list-picker__menu"
                      data-testid="film-watch-year-menu"
                    >
                      <button
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': !filmWatchYear }"
                        data-testid="film-watch-year-option-empty"
                        type="button"
                        @click="selectFilmWatchYear('')"
                      >
                        Select year
                      </button>
                      <button
                        v-for="year in filmWatchYearOptions"
                        :key="year"
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': year === filmWatchYear }"
                        :data-testid="`film-watch-year-option-${year}`"
                        type="button"
                        @click="selectFilmWatchYear(year)"
                      >
                        {{ year }}
                      </button>
                    </div>
                  </div>
                  <select
                    v-model="filmWatchYear"
                    data-testid="film-watch-year"
                    :disabled="filmWatchModalPending"
                    class="film-list-picker__native"
                    tabindex="-1"
                    aria-hidden="true"
                  >
                    <option value="">Select year</option>
                    <option v-for="year in filmWatchYearOptions" :key="year" :value="year">
                      {{ year }}
                    </option>
                  </select>
                </label>
                <label
                  class="film-watch-modal__field"
                  :class="{ 'film-watch-modal__field--picker-open': filmWatchDatePickerOpen === 'month' }"
                >
                  <span>Month</span>
                  <div
                    class="film-list-picker"
                    :class="{ 'film-list-picker--open': filmWatchDatePickerOpen === 'month' }"
                  >
                    <button
                      class="film-list-picker__toggle"
                      :class="{ 'film-list-picker__toggle--open': filmWatchDatePickerOpen === 'month' }"
                      data-testid="film-watch-month-picker"
                      type="button"
                      :disabled="filmWatchModalPending"
                      @click="toggleFilmWatchDatePicker('month')"
                    >
                      <span class="film-list-picker__label">{{ selectedFilmWatchMonthLabel }}</span>
                      <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                    </button>
                    <div
                      v-if="filmWatchDatePickerOpen === 'month'"
                      class="film-list-picker__menu"
                      data-testid="film-watch-month-menu"
                    >
                      <button
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': !filmWatchMonth }"
                        data-testid="film-watch-month-option-empty"
                        type="button"
                        @click="selectFilmWatchMonth('')"
                      >
                        Select month
                      </button>
                      <button
                        v-for="month in filmWatchMonthOptions"
                        :key="month.value"
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': month.value === filmWatchMonth }"
                        :data-testid="`film-watch-month-option-${month.value}`"
                        type="button"
                        @click="selectFilmWatchMonth(month.value)"
                      >
                        {{ month.label }}
                      </button>
                    </div>
                  </div>
                  <select
                    v-model="filmWatchMonth"
                    data-testid="film-watch-month"
                    :disabled="filmWatchModalPending"
                    class="film-list-picker__native"
                    tabindex="-1"
                    aria-hidden="true"
                  >
                    <option value="">Select month</option>
                    <option v-for="month in filmWatchMonthOptions" :key="month.value" :value="month.value">
                      {{ month.label }}
                    </option>
                  </select>
                </label>
                <label
                  class="film-watch-modal__field"
                  :class="{ 'film-watch-modal__field--picker-open': filmWatchDatePickerOpen === 'day' }"
                >
                  <span>Day</span>
                  <div
                    class="film-list-picker"
                    :class="{ 'film-list-picker--open': filmWatchDatePickerOpen === 'day' }"
                  >
                    <button
                      class="film-list-picker__toggle"
                      :class="{ 'film-list-picker__toggle--open': filmWatchDatePickerOpen === 'day' }"
                      data-testid="film-watch-day-picker"
                      type="button"
                      :disabled="filmWatchModalPending"
                      @click="toggleFilmWatchDatePicker('day')"
                    >
                      <span class="film-list-picker__label">{{ selectedFilmWatchDayLabel }}</span>
                      <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                    </button>
                    <div
                      v-if="filmWatchDatePickerOpen === 'day'"
                      class="film-list-picker__menu"
                      data-testid="film-watch-day-menu"
                    >
                      <button
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': !filmWatchDay }"
                        data-testid="film-watch-day-option-empty"
                        type="button"
                        @click="selectFilmWatchDay('')"
                      >
                        Select day
                      </button>
                      <button
                        v-for="day in filmWatchDayOptions"
                        :key="day"
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': day === filmWatchDay }"
                        :data-testid="`film-watch-day-option-${day}`"
                        type="button"
                        @click="selectFilmWatchDay(day)"
                      >
                        {{ day }}
                      </button>
                    </div>
                  </div>
                  <select
                    v-model="filmWatchDay"
                    data-testid="film-watch-day"
                    :disabled="filmWatchModalPending"
                    class="film-list-picker__native"
                    tabindex="-1"
                    aria-hidden="true"
                  >
                    <option value="">Select day</option>
                    <option v-for="day in filmWatchDayOptions" :key="day" :value="day">
                      {{ day }}
                    </option>
                  </select>
                </label>
                <button
                  class="film-watch-modal__today"
                  data-testid="film-watch-today"
                  type="button"
                  :disabled="filmWatchModalPending"
                  @click="setFilmWatchToday"
                >
                  Today
                </button>
              </div>
            </section>

            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Film format</h3>
                <p>Optional</p>
              </div>
              <div class="film-watch-modal__format-groups">
                <div
                  v-for="(formatGroup, groupIndex) in filmWatchFormatGroups"
                  :key="`film-watch-format-group-${groupIndex}`"
                  class="film-watch-modal__format-group"
                  :class="{ 'film-watch-modal__format-group--two-col': formatGroup.columns === 2 }"
                >
                  <label
                    v-for="formatOption in formatGroup.items"
                    :key="formatOption.value"
                    class="film-watch-modal__choice"
                  >
                    <input
                      v-model="filmWatchFormat"
                      :data-testid="`film-watch-format-${formatOption.value}`"
                      type="radio"
                      name="film-watch-format"
                      :value="formatOption.value"
                      :disabled="filmWatchModalPending"
                    >
                    <span>{{ formatOption.label }}</span>
                  </label>
                </div>
              </div>
            </section>

            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Notes to self</h3>
                <p>Optional</p>
              </div>
              <textarea
                v-model="filmWatchDescription"
                class="film-watch-modal__textarea"
                data-testid="film-watch-description"
                maxlength="300"
                rows="5"
                :disabled="filmWatchModalPending"
              ></textarea>
              <div class="film-watch-modal__notes-footer">
                <p class="film-watch-modal__counter">
                  {{ filmWatchRemainingCharacters }} characters left.
                </p>
                <label class="film-watch-modal__checkbox film-watch-modal__notes-public">
                  <input
                    v-model="filmWatchPublicDescription"
                    data-testid="film-watch-public"
                    type="checkbox"
                    :disabled="filmWatchModalPending"
                  >
                  <span>Public</span>
                </label>
              </div>
            </section>

            <div
              class="film-watch-modal__actions"
              :class="{ 'film-watch-modal__actions--end': filmWatchEditing }"
            >
              <button
                v-if="!filmWatchEditing"
                class="film-watch-modal__action film-watch-modal__action--secondary"
                data-testid="film-watch-clear"
                type="button"
                :disabled="filmWatchModalPending"
                @click="clearFilmWatchForm"
              >
                Clear form
              </button>
              <div class="film-watch-modal__action-group">
                <button
                  class="film-watch-modal__action film-watch-modal__action--secondary"
                  type="button"
                  :disabled="filmWatchModalPending"
                  @click="closeFilmWatchModal"
                >
                  Cancel
                </button>
                <button
                  class="film-watch-modal__action"
                  data-testid="film-watch-save"
                  type="submit"
                  :disabled="filmWatchModalPending"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
    <MobileSearchOverlay :open="mobileSearchOpen" @close="closeMobileSearchOverlay" />
  </main>
</template>

<script setup>
import CuratedListLabel from '../components/CuratedListLabel.vue'
import { usePageViewTracking } from '../analytics/pageViews'
import { computed, defineComponent, h, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import CommentsPanel from '../components/CommentsPanel.vue'
import CompactMobilePagination from '../components/CompactMobilePagination.vue'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import NotFoundView from './NotFoundView.vue'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import { useAuthStore } from '../stores/auth'
import { buildLoginPath } from '../utils/auth'
import { compactPaginationItems } from '../utils/pagination'
import { clipOverviewWithSuffix, hasExpandableOverview } from '../utils/overview'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'
import { buildMetaDescription, useMetaDescription } from '../utils/metaDescription'

const route = useRoute()
const capturePageView = usePageViewTracking()
const auth = useAuthStore()
auth.initFromStorage()
const slug = computed(() => route.params.slug || 'lion')
const basePath = computed(() => `/films/${slug.value}`)
const loginPath = computed(() => buildLoginPath(route.fullPath || basePath.value))
const isAuthenticated = computed(() => auth.isAuthenticated.value)
const authUsername = computed(() => auth.state.username || 'Account')
const activeSection = computed(() => {
  const section = route.params.section || 'overview'
  return section === 'discussion' ? 'comments' : section
})
const imageBase = '/examples/new-ui/single-film/images'
const defaultBackgroundImage = '/media/films/backgrounds/default-bg.jpg'
const posterPlaceholderImage = '/static/films/img/placeholder-film-camera.svg'
const prototypeOverview = [
  'A five-year-old Indian boy gets lost on the streets of Calcutta, thousands of kilometers from home.',
  'He survives many challenges before being adopted by a couple in Australia; 25 years later, he sets out to find his lost family.',
].join(' ')
const castPlaceholderImage = posterPlaceholderImage
const mobileSearchOpen = ref(false)
const filmWatchlistPending = ref(false)
const filmWatchlisted = ref(false)
const filmFavouritePending = ref(false)
const filmFavourited = ref(false)
const filmRatingPending = ref(false)
const filmRating = ref(null)
const filmWatched = ref(false)
const filmNotFound = ref(false)
const anonymousYouSection = computed(() => activeSection.value === 'you' && !isAuthenticated.value)
const showFilmNotFound = computed(() => filmNotFound.value || anonymousYouSection.value)
const filmNotFoundHeading = computed(() =>
  anonymousYouSection.value ? 'Page was not found' : 'Film not found',
)
const filmNotFoundMessage = computed(() =>
  anonymousYouSection.value
    ? 'The page you were looking for could not be found.'
    : 'The film you were looking for could not be found.',
)
const filmListModalOpen = ref(false)
const filmListModalPending = ref(false)
const filmListModalLoading = ref(false)
const filmListModalError = ref('')
const filmListCreateMode = ref(false)
const filmListOptions = ref([])
const filmListSuccessTitle = ref('')
const filmListSuccessDismissing = ref(false)
const filmListSelectedListId = ref('')
const filmListPickerOpen = ref(false)
const filmListCreateTitle = ref('')
const filmListCreateDescription = ref('')
const filmListCreatePublic = ref(true)
const filmListsPageLoading = ref(false)
const filmListsPageLoaded = ref(false)
const filmListsPageError = ref('')
const filmListsPageNumber = ref(1)
const filmListsPageSize = ref(10)
const filmListsPageCount = ref(0)
const filmListsNumPages = ref(1)
const filmListsPageEntries = ref([])
const filmWatchModalOpen = ref(false)
const filmWatchModalPending = ref(false)
const filmWatchModalError = ref('')
const filmWatchEditingId = ref(null)
const filmWatchExistingCount = ref(0)
const filmLatestWatch = ref(null)
const filmYouWatches = ref([])
const filmYouWatchesLoading = ref(false)
const filmYouWatchesLoaded = ref(false)
const filmYouWatchesError = ref('')
const latestOverviewCommentsLoaded = ref(false)
const latestOverviewComments = ref([])
const filmWatchDatePickerOpen = ref('')
const filmWatchYear = ref('')
const filmWatchMonth = ref('')
const filmWatchDay = ref('')
const filmWatchFormat = ref('')
const filmWatchDescription = ref('')
const filmWatchPublicDescription = ref(false)
const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const monthOptionLabels = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const filmWatchMonthOptions = monthOptionLabels.map((label, index) => ({
  value: String(index + 1).padStart(2, '0'),
  label,
}))
const filmWatchFormatGroups = [
  {
    columns: 1,
    items: [
      { value: 'streaming', label: 'Streaming' },
    ],
  },
  {
    columns: 2,
    items: [
      { value: 'blu-ray', label: 'Blu-ray' },
      { value: 'dvd', label: 'DVD' },
      { value: '4k-ultra-hd', label: '4K Ultra HD' },
      { value: 'hd-dvd', label: 'HD-DVD' },
      { value: 'vhs', label: 'VHS' },
    ],
  },
  {
    columns: 2,
    items: [
      { value: 'theater', label: 'In theater' },
      { value: 'tv', label: 'TV broadcast' },
    ],
  },
  {
    columns: 1,
    items: [
      { value: 'digital', label: 'Digital copy' },
    ],
  },
]
const prototypeMainCast = [
  {
    image: `${imageBase}/cast/patel.jpg`,
    actor: 'Dev Patel',
    role: 'Saroo Brierley',
    url: '#',
  },
  {
    image: `${imageBase}/cast/kidman.jpg`,
    actor: 'Nicole Kidman',
    role: 'Sue Brierley',
    url: '#',
  },
  {
    image: `${imageBase}/cast/mara.jpg`,
    actor: 'Rooney Mara',
    role: 'Lucy',
    url: '#',
  },
  {
    image: `${imageBase}/cast/unavailable.svg`,
    actor: 'Sunny Pawar',
    role: 'Young Saroo',
    url: '#',
  },
  {
    image: `${imageBase}/cast/bose.jpg`,
    actor: 'Priyanka Bose',
    role: 'Kamla',
    url: '#',
  },
  {
    image: `${imageBase}/cast/bose.jpg`,
    actor: 'Priyanka Bose',
    role: 'Kamla',
    url: '#',
  },
]
const prototypeDirectors = [
  {
    name: 'Garth Davis',
    url: '#',
  },
  {
    name: 'Garth Davis',
    url: '#',
  },
]
const creditImage = (fileName) => `${imageBase}/cast/${fileName}`
const prototypeCreditsDirectors = [
  { image: creditImage('unavailable.svg'), name: 'Garth Davis', role: 'Director', url: '#' },
]
const prototypeCreditsWriters = [
  { image: creditImage('unavailable.svg'), name: 'Luke Davis', role: 'Writer', url: '#' },
  { image: creditImage('unavailable.svg'), name: 'Saroo Brierley', role: 'Writer', url: '#' },
]
const prototypeCreditsCrew = [
  { image: creditImage('unavailable.svg'), name: "Dustin O'Halloran", role: 'Crew member', url: '#' },
]
const prototypeCreditsCast = [
  { image: creditImage('unavailable.svg'), name: 'Sunny Pawar', role: 'Young Saroo', url: '#' },
  { image: creditImage('bose.jpg'), name: 'Priyanka Bose', role: 'Kamala', url: '#' },
  { image: creditImage('unavailable.svg'), name: 'Tannishtha Chatterjee', role: 'Noor', url: '#' },
  { image: creditImage('nawazuddin.jpg'), name: 'Nawazuddin Siddiqui', role: 'Rama', url: '#' },
  { image: creditImage('unavailable.svg'), name: 'Riddhi Sen', role: 'Café Man', url: '#' },
  { image: creditImage('unavailable.svg'), name: 'Deepti Naval', role: 'Mrs. Sood', url: '#' },
  { image: creditImage('unavailable.svg'), name: 'Menik Gooneratne', role: 'Swarmina', url: '#' },
  { image: creditImage('david.jpg'), name: 'David Wenham', role: 'John Brierley', url: '#' },
  { image: creditImage('kidman.jpg'), name: 'Nicole Kidman', role: 'Sue Brierley', url: '#' },
  { image: creditImage('patel.jpg'), name: 'Dev Patel', role: 'Saroo Brierley', url: '#' },
  { image: creditImage('unavailable.svg'), name: 'Benjamin Rigby', role: 'Waiter', url: '#' },
  { image: creditImage('unavailable.svg'), name: 'Divian Ladwa', role: 'Mantosh Brierley', url: '#' },
  { image: creditImage('mara.jpg'), name: 'Rooney Mara', role: 'Lucy', url: '#' },
  { image: creditImage('unavailable.svg'), name: 'Daniela Farinacci', role: 'Tutor', url: '#' },
  { image: creditImage('pallavi.jpg'), name: 'Pallavi Sharda', role: 'Prama', url: '#' },
  { image: creditImage('unavailable.svg'), name: 'Sachin Joab', role: 'Bharat', url: '#' },
  { image: creditImage('arka.jpg'), name: 'Arka Das', role: 'Sami', url: '#' },
  { image: creditImage('luke.jpg'), name: 'Eamon Farren', role: 'Luke', url: '#' },
  { image: creditImage('anna.jpg'), name: 'Anna Samson', role: 'Cute Bar Maid', url: '#' },
]

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}

function createPrototypeFilmState() {
  return {
    id: null,
    title: 'Lion',
    year: '1994',
    overview: prototypeOverview,
    releaseDate: '2016-11-24',
    mainCast: prototypeMainCast,
    creditsCast: prototypeCreditsCast,
    creditsDirectors: prototypeCreditsDirectors,
    creditsWriters: prototypeCreditsWriters,
    creditsCrew: prototypeCreditsCrew,
    directors: prototypeDirectors,
    runtimeText: '118 min.',
    genres: ['Drama', 'Comedy'],
    spokenLanguages: ['Bengali', 'English', 'Hindi'],
    productionCountries: ['USA', 'Australia', 'UK'],
    imdbUrl: 'https://www.imdb.com/title/tt1234567',
    canAccessBadmin: false,
    showAds: true,
    posterUrl: `${imageBase}/lion.jpg`,
    backgroundUrl: `${imageBase}/lion-bg.jpg`,
    mobileBackgroundUrl: '',
    hasBackground: true,
    overviewLists: [],
  }
}

function createEmptyFilmState() {
  return {
    id: null,
    title: '',
    year: null,
    overview: '',
    releaseDate: null,
    mainCast: [],
    creditsCast: [],
    creditsDirectors: [],
    creditsWriters: [],
    creditsCrew: [],
    directors: [],
    runtimeText: '',
    genres: [],
    spokenLanguages: [],
    productionCountries: [],
    imdbUrl: '',
    canAccessBadmin: false,
    showAds: true,
    posterUrl: '',
    backgroundUrl: '',
    mobileBackgroundUrl: '',
    hasBackground: false,
    overviewLists: [],
  }
}

function createFallbackFilmState(currentSlug) {
  return currentSlug === 'lion' ? createPrototypeFilmState() : createEmptyFilmState()
}

const hoverRating = ref(0)
const filmLoaded = ref(false)
const filmPosterStatus = ref('loading')
const filmOverviewLoaded = ref(false)
const filmCreditsLoaded = ref(false)
const creditsFilter = ref('')
const filmOverviewExpanded = ref(false)
const film = ref(createFallbackFilmState(slug.value))
const hasLoadedFilmPayload = computed(() => filmLoaded.value && !filmNotFound.value)
const filmTitle = computed(() => film.value.title || '')
const releaseYear = computed(() => film.value.year)
const documentTitle = computed(() => buildDocumentTitle([
  releaseYear.value ? `${filmTitle.value} (${releaseYear.value})` : filmTitle.value,
], activeSection.value))
const filmOverview = computed(() => film.value.overview || '')
const hasExpandableFilmOverview = computed(() => hasExpandableOverview(filmOverview.value))
const displayedFilmOverview = computed(() => {
  if (!hasExpandableFilmOverview.value || filmOverviewExpanded.value) {
    return filmOverview.value
  }
  return clipOverviewWithSuffix(filmOverview.value, undefined, '')
})
const releaseDateDisplay = computed(() => formatReleaseDate(film.value.releaseDate))
const directors = computed(() => {
  const entries = Array.isArray(film.value.directors) ? film.value.directors : []
  return entries.filter((director) => director.name)
})
const mainCast = computed(() => {
  const cast = Array.isArray(film.value.mainCast) ? film.value.mainCast : []
  return cast.slice(0, 6)
})
const mainCastRows = computed(() => {
  const rows = []
  for (let index = 0; index < mainCast.value.length; index += 2) {
    rows.push(mainCast.value.slice(index, index + 2))
  }
  return rows
})
const creditsCast = computed(() => {
  const cast = Array.isArray(film.value.creditsCast) ? film.value.creditsCast : []
  return cast.filter((member) => member.name)
})
const creditsDirectors = computed(() => {
  const entries = Array.isArray(film.value.creditsDirectors) ? film.value.creditsDirectors : []
  return entries.filter((member) => member.name)
})
const creditsWriters = computed(() => {
  const entries = Array.isArray(film.value.creditsWriters) ? film.value.creditsWriters : []
  return entries.filter((member) => member.name)
})
const creditsCrew = computed(() => {
  const entries = Array.isArray(film.value.creditsCrew) ? film.value.creditsCrew : []
  return entries.filter((member) => member.name)
})
const creditsFilterTerm = computed(() => creditsFilter.value.trim().toLowerCase())
const filteredCreditsCast = computed(() => filterCreditsMembers(creditsCast.value))
const filteredCreditsDirectors = computed(() => filterCreditsMembers(creditsDirectors.value))
const filteredCreditsWriters = computed(() => filterCreditsMembers(creditsWriters.value))
const filteredCreditsCrew = computed(() => filterCreditsMembers(creditsCrew.value))
const creditsAvailable = computed(() =>
  creditsCast.value.length > 0 ||
  creditsDirectors.value.length > 0 ||
  creditsWriters.value.length > 0 ||
  creditsCrew.value.length > 0,
)
const showCreditsCastSection = computed(() =>
  creditsAvailable.value &&
  (filteredCreditsCast.value.length > 0 || (creditsCast.value.length === 0 && !creditsFilterTerm.value)),
)
const creditsCastTitle = computed(() => `Cast (${filteredCreditsCast.value.length})`)
const creditsSections = computed(() => {
  const sections = []
  if (filteredCreditsDirectors.value.length > 0) {
    sections.push({
      id: 'directing',
      title: `Directing (${filteredCreditsDirectors.value.length})`,
      titleTestId: 'credits-directing-title',
      memberTestId: 'credits-directing-member',
      members: filteredCreditsDirectors.value,
    })
  }
  if (filteredCreditsWriters.value.length > 0) {
    sections.push({
      id: 'writing',
      title: `Writing (${filteredCreditsWriters.value.length})`,
      titleTestId: 'credits-writing-title',
      memberTestId: 'credits-writing-member',
      members: filteredCreditsWriters.value,
    })
  }
  if (filteredCreditsCrew.value.length > 0) {
    sections.push({
      id: 'crew',
      title: `Crew (${filteredCreditsCrew.value.length})`,
      titleTestId: 'credits-crew-title',
      memberTestId: 'credits-crew-member',
      members: filteredCreditsCrew.value,
    })
  }
  return sections
})
const titleLengthClass = computed(() => {
  const titleLength = filmTitle.value.length
  if (titleLength > 150) return 'item-title--extreme'
  if (titleLength > 120) return 'item-title--very-long'
  if (titleLength > 75) return 'item-title--long'
  return null
})
const runtimeText = computed(() => film.value.runtimeText || '')
const genres = computed(() => normalizeStringList(film.value.genres))
const spokenLanguages = computed(() => normalizeStringList(film.value.spokenLanguages))
const productionCountries = computed(() => normalizeStringList(film.value.productionCountries))
const imdbUrl = computed(() => film.value.imdbUrl || '')
const canAccessBadmin = computed(() => film.value.canAccessBadmin === true && Boolean(film.value.id))
const filmAdminUrl = computed(() => `/badmin/films/${film.value.id}/edit`)
const showFilmAds = computed(() => film.value.showAds !== false)
const relatedResourceParts = computed(() => {
  const resources = []
  if (imdbUrl.value) {
    resources.push({ label: 'IMDb.com', url: imdbUrl.value })
  }
  if (canAccessBadmin.value) {
    resources.push({ label: 'Admin', url: filmAdminUrl.value, testId: 'film-admin-link' })
  }
  return resources.map((resource) => ['resource', resource])
})
const backgroundUrl = computed(() => film.value.backgroundUrl || '')
const mobileBackgroundUrl = computed(() => film.value.mobileBackgroundUrl || '')
const filmHasBackground = computed(() => film.value.hasBackground === true)
const heroBackgroundStyle = computed(() => (
  backgroundUrl.value
    ? { backgroundImage: `url('${backgroundUrl.value}')` }
    : undefined
))
const filmPosterLoading = computed(() => filmPosterStatus.value === 'loading')
const filmPosterImageSrc = computed(() => film.value.posterUrl || '')
const filmPosterFrameClasses = computed(() => [
  'image-load-frame',
  'image-load-frame--poster',
  filmPosterStatus.value === 'loading' ? 'image-load-frame--loading' : null,
  filmPosterStatus.value === 'loaded' ? 'image-load-frame--loaded' : null,
  filmPosterStatus.value === 'fallback' ? 'image-load-frame--fallback' : null,
])
const filmPosterImageClasses = computed(() => [
  'hero__poster',
  'image-load-frame__image',
  filmPosterStatus.value !== 'loading' ? 'image-load-frame__image--visible' : null,
])

function resetFilmPosterLoading(src = film.value.posterUrl) {
  if (!src) {
    filmPosterStatus.value = 'loading'
    return
  }
  filmPosterStatus.value = src === posterPlaceholderImage ? 'fallback' : 'loading'
}

function handleFilmPosterLoad() {
  filmPosterStatus.value = film.value.posterUrl === posterPlaceholderImage
    ? 'fallback'
    : 'loaded'
}

function handleFilmPosterError() {
  if (film.value.posterUrl !== posterPlaceholderImage) {
    film.value = {
      ...film.value,
      posterUrl: posterPlaceholderImage,
    }
    return
  }
  filmPosterStatus.value = 'fallback'
}

const overviewLists = computed(() =>
  Array.isArray(film.value.overviewLists) ? film.value.overviewLists : [],
)
const filmListsHeading = computed(() => `Lists with this film (${filmListsPageCount.value})`)
const filmLists = computed(() => filmListsPageEntries.value)
const showFilmListsPagination = computed(() => filmListsNumPages.value > 1)
const canGoToPreviousFilmListsPage = computed(() => filmListsPageNumber.value > 1)
const canGoToNextFilmListsPage = computed(() => filmListsPageNumber.value < filmListsNumPages.value)
const filmListsPages = computed(() => (
  compactPaginationItems(filmListsPageNumber.value, filmListsNumPages.value)
))
const selectedFilmListLabel = computed(() => {
  const selectedId = String(filmListSelectedListId.value || '').trim()
  if (!selectedId) {
    return 'Choose list...'
  }
  const selectedList = filmListOptions.value.find((entry) => String(entry.id) === selectedId)
  return selectedList?.title || 'Choose list...'
})
const isFilmFavourited = computed(() => filmFavourited.value)
const isFilmWatchlisted = computed(() => filmWatchlisted.value)
const isFilmWatched = computed(() => filmWatched.value)
const isFilmRated = computed(() => Number.isInteger(filmRating.value) && filmRating.value >= 1 && filmRating.value <= 10)
const watchButtonText = computed(() => (isFilmWatched.value ? 'Watched' : 'Mark as watched'))
const watchButtonShortText = computed(() => (isFilmWatched.value ? 'Watched' : 'Set as watched'))
const watchButtonIconClass = computed(() => (isFilmWatched.value ? 'ion-checkmark' : 'ion-plus'))
const watchlistButtonText = computed(() => (isFilmWatchlisted.value ? 'Watchlisted' : 'Add to watchlist'))
const watchlistButtonShortText = computed(() => (isFilmWatchlisted.value ? 'Watchlisted' : 'Watchlist'))
const favouriteButtonText = computed(() => (isFilmFavourited.value ? 'Favourited' : 'Add to favourites'))
const favouriteButtonShortText = computed(() => (isFilmFavourited.value ? 'Favourited' : 'Favourite'))
const ratingLabelPrefix = computed(() => (isFilmRated.value ? 'Rated:' : 'Rate:'))
const ratingLabelValue = computed(() => (isFilmRated.value ? `${filmRating.value} / 10` : '/ 10'))
const filmWatchEditing = computed(() => filmWatchEditingId.value !== null)
const filmWatchModalTitle = computed(() =>
  releaseYear.value ? `${filmTitle.value} (${releaseYear.value})` : filmTitle.value,
)
const filmWatchCurrentUtcYear = new Date().getUTCFullYear()
const filmWatchYearOptions = Array.from(
  { length: filmWatchCurrentUtcYear - 1899 },
  (_, index) => String(filmWatchCurrentUtcYear - index),
)
const filmWatchRemainingCharacters = computed(() => Math.max(0, 300 - filmWatchDescription.value.length))
const filmWatchHasExisting = computed(() => filmWatchExistingCount.value > 0 || filmWatched.value)
const filmWatchExistingLabel = computed(() =>
  filmWatchExistingCount.value > 0 ? pluralize(filmWatchExistingCount.value, 'watch') : 'watch',
)
const latestFilmWatchText = computed(() => formatLatestFilmWatchText(filmLatestWatch.value))
const filmYouWatchCount = computed(() => filmYouWatches.value.length)
const filmYouHeading = computed(() => `Watches (${filmYouWatchCount.value})`)
const filmWatchSelectedDayCount = computed(() => {
  if (!filmWatchYear.value || !filmWatchMonth.value) {
    return 31
  }
  return new Date(
    Date.UTC(
      Number.parseInt(filmWatchYear.value, 10),
      Number.parseInt(filmWatchMonth.value, 10),
      0,
    ),
  ).getUTCDate()
})
const filmWatchDayOptions = computed(() =>
  Array.from({ length: filmWatchSelectedDayCount.value }, (_, index) =>
    String(index + 1).padStart(2, '0'),
  ),
)
const selectedFilmWatchYearLabel = computed(() => filmWatchYear.value || 'Select year')
const selectedFilmWatchMonthLabel = computed(() => (
  filmWatchMonthOptions.find((month) => month.value === filmWatchMonth.value)?.label || 'Select month'
))
const selectedFilmWatchDayLabel = computed(() => filmWatchDay.value || 'Select day')
const assets = {
  logo: '/static/spa/branding/logo.png',
}

function formatReleaseDate(releaseDate) {
  const parsedDate = String(releaseDate || '').match(/^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?/)
  const year = parsedDate?.[1] || ''
  if (!year) return null

  const month = parsedDate?.[2]
  if (!month || month === '00') {
    return { prefix: '', year }
  }

  const monthName = monthNames[Number(month) - 1]
  if (!monthName) {
    return { prefix: '', year }
  }

  const day = parsedDate?.[3]
  if (!day || day === '00') {
    return { prefix: `${monthName}, `, year }
  }

  const dayNumber = Number(day)
  if (!Number.isInteger(dayNumber) || dayNumber < 1 || dayNumber > 31) {
    return { prefix: `${monthName}, `, year }
  }

  return { prefix: `${monthName} ${dayNumber}, `, year }
}

function normalizeLatestFilmWatch(payload) {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const watched = String(payload.watched || '').trim()
  const createdAt = String(payload.created_at || '').trim()
  if (!watched && !createdAt) {
    return null
  }

  return { watched, createdAt }
}

function normalizeFilmYouWatch(payload) {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  return {
    id: payload.id,
    watched: String(payload.watched || '').trim(),
    createdAt: String(payload.created_at || '').trim(),
    format: String(payload.format || '').trim(),
    formatLabel: String(payload.format_label || '').trim(),
    description: String(payload.description || '').trim(),
    publicDescription: Boolean(payload.public_description),
  }
}

function formatFilmWatchDateValue(value) {
  const watchedValue = String(value || '').trim()
  const watchedMatch = watchedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!watchedMatch) {
    return ''
  }

  const [, year, month, day] = watchedMatch
  if (month === '00') {
    return year
  }

  const monthName = monthNames[Number(month) - 1]
  if (!monthName) {
    return year
  }

  if (day === '00') {
    return `${monthName}, ${year}`
  }

  return `${monthName} ${Number(day)}, ${year}`
}

function formatLatestFilmWatchText(latestWatch) {
  if (!latestWatch) return ''

  const watchedText = formatFilmWatchDateValue(latestWatch.watched)
  if (watchedText) {
    return watchedText
  }

  const createdAt = String(latestWatch.createdAt || '').trim()
  if (!createdAt) return ''

  const createdAtDate = new Date(createdAt)
  if (Number.isNaN(createdAtDate.getTime())) return ''

  return `Added ${monthNames[createdAtDate.getUTCMonth()]} ${createdAtDate.getUTCDate()}, ${createdAtDate.getUTCFullYear()}`
}

function formatCreatedAtDateText(createdAt) {
  const value = String(createdAt || '').trim()
  if (!value) return ''

  const createdAtDate = new Date(value)
  if (Number.isNaN(createdAtDate.getTime())) return ''

  return `${monthNames[createdAtDate.getUTCMonth()]} ${createdAtDate.getUTCDate()}, ${createdAtDate.getUTCFullYear()}`
}

function filmYouWatchDateText(watch) {
  const watchedText = formatFilmWatchDateValue(watch?.watched)
  if (watchedText) {
    return watchedText
  }

  const createdAtText = formatCreatedAtDateText(watch?.createdAt)
  if (!createdAtText) {
    return 'No date given'
  }

  return `${createdAtText} (added)`
}

function filmYouWatchFormatText(watch) {
  const label = watch?.formatLabel || watch?.format || ''
  return label === 'Streaming' ? 'Streamed' : label
}

function filmYouWatchSortValue(watch) {
  return String(watch?.watched || watch?.createdAt || '').trim()
}

function compareFilmYouWatches(a, b) {
  const dateCompare = filmYouWatchSortValue(b).localeCompare(filmYouWatchSortValue(a))
  if (dateCompare !== 0) {
    return dateCompare
  }

  return Number(b?.id || 0) - Number(a?.id || 0)
}

function replaceFilmYouWatch(updatedWatch) {
  const normalized = normalizeFilmYouWatch(updatedWatch)
  if (!normalized) {
    return
  }

  filmYouWatches.value = filmYouWatches.value.map((watch) =>
    watch.id === normalized.id ? normalized : watch,
  ).sort(compareFilmYouWatches)
}

function removeFilmYouWatchFromList(watchId) {
  filmYouWatches.value = filmYouWatches.value.filter((watch) => watch.id !== watchId)
  filmYouWatchesLoaded.value = true
}

function applyFilmYouWatchesPayload(data) {
  const watches = data && Array.isArray(data.watches) ? data.watches : []
  filmYouWatches.value = watches
    .map(normalizeFilmYouWatch)
    .filter(Boolean)
    .sort(compareFilmYouWatches)
  filmYouWatchesLoaded.value = true
  filmYouWatchesLoading.value = false
  filmYouWatchesError.value = ''
}

function normalizeMainCast(castEntries) {
  if (!Array.isArray(castEntries)) return []

  return castEntries.slice(0, 6).map((cast) => ({
    image: cast.image || castPlaceholderImage,
    actor: cast.actor || '',
    role: cast.role || '',
    url: cast.url || '#',
  }))
}

function normalizeCreditsCast(castEntries) {
  if (!Array.isArray(castEntries)) return []

  return castEntries
    .map((cast) => ({
      image: cast.image || castPlaceholderImage,
      name: cast.name || cast.actor || '',
      role: cast.role || '',
      url: cast.url || '#',
    }))
    .filter((cast) => cast.name)
}

function normalizeDirectors(directorEntries) {
  if (!Array.isArray(directorEntries)) return []

  return directorEntries
    .filter((director) => director?.name)
    .map((director) => ({
      name: director.name,
      url: director.url || '#',
    }))
}

function normalizeCreditDirectors(directorEntries) {
  if (!Array.isArray(directorEntries)) return []

  return directorEntries
    .filter((director) => director?.name)
    .map((director) => ({
      image: director.image || castPlaceholderImage,
      name: director.name,
      role: 'Director',
      url: director.url || '#',
    }))
}

function normalizeCreditWriters(writerEntries) {
  if (!Array.isArray(writerEntries)) return []

  return writerEntries
    .filter((writer) => writer?.name)
    .map((writer) => ({
      image: writer.image || castPlaceholderImage,
      name: writer.name,
      role: writer.role || '',
      url: writer.url || '#',
    }))
}

function normalizeCreditCrew(crewEntries) {
  if (!Array.isArray(crewEntries)) return []

  return crewEntries
    .filter((member) => member?.name)
    .map((member) => ({
      image: member.image || castPlaceholderImage,
      name: member.name,
      role: member.role || 'Crew member',
      url: member.url || '#',
    }))
}

function normalizeOverviewListItem(item) {
  if (!item || typeof item !== 'object') {
    return null
  }

  const posterUrl = String(item.poster_url || '').trim()
  const title = String(item.title || '').trim()
  const year = String(item.year || '').trim()
  if (!posterUrl || !title) {
    return null
  }
  const placeholderLabel = year ? `${title} (${year})` : title
  const showPlaceholderLabel = posterUrl.includes('placeholder-film-camera.svg')

  return {
    id: item.id ?? `${title}-${posterUrl}`,
    title,
    year,
    posterUrl,
    placeholderLabel,
    showPlaceholderLabel,
  }
}

function normalizeFilmList(listEntry, itemLimit = 4) {
  if (!listEntry || typeof listEntry !== 'object') {
    return null
  }

  const title = String(listEntry.title || '').trim()
  if (!title) {
    return null
  }

  const items = Array.isArray(listEntry.items)
    ? listEntry.items.map(normalizeOverviewListItem).filter(Boolean).slice(0, itemLimit)
    : []

  return {
    id: listEntry.id ?? title,
    title,
    path: listEntry.is_curated ? listEntry.url : buildFilmListPath(listEntry.id, title),
    official: Boolean(listEntry.official),
    is_curated: Boolean(listEntry.is_curated),
    ownerName: String(listEntry.owner_name || '').trim(),
    ownerHref: buildFilmsomniacPath(listEntry.owner_name),
    items,
  }
}

function slugifyListTitle(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function buildFilmListPath(listId, title) {
  const normalizedId = String(listId || '').trim()
  if (!normalizedId) {
    return '#'
  }
  const slug = slugifyListTitle(title) || 'list'
  return `/lists/film/${normalizedId}-${slug}`
}

function buildFilmsomniacPath(username) {
  const normalizedUsername = String(username || '').trim()
  return normalizedUsername ? `/filmsomniacs/${encodeURIComponent(normalizedUsername)}` : '#'
}

function filmListPath(filmList) {
  return String(filmList?.path || buildFilmListPath(filmList?.id, filmList?.title))
}

function normalizeOverviewList(listEntry) {
  return normalizeFilmList(listEntry, 4)
}

function normalizeFilmListsPageEntry(listEntry) {
  return normalizeFilmList(listEntry, 8)
}

function prependOverviewList(listEntry) {
  const normalized = normalizeOverviewList(listEntry)
  if (!normalized) {
    return
  }

  const existing = Array.isArray(film.value.overviewLists) ? film.value.overviewLists : []
  const nextOverviewLists = [
    normalized,
    ...existing.filter((entry) => String(entry.id) !== String(normalized.id)),
  ].slice(0, 2)

  film.value = {
    ...film.value,
    overviewLists: nextOverviewLists,
  }
}

function prependFilmListsPageEntry(listEntry) {
  const normalized = normalizeFilmListsPageEntry(listEntry)
  if (!normalized) {
    return
  }

  const existing = Array.isArray(filmListsPageEntries.value) ? filmListsPageEntries.value : []
  const alreadyExists = existing.some((entry) => String(entry.id) === String(normalized.id))
  filmListsPageNumber.value = 1
  filmListsPageEntries.value = [
    normalized,
    ...existing.filter((entry) => String(entry.id) !== String(normalized.id)),
  ].slice(0, filmListsPageSize.value)
  filmListsPageCount.value = alreadyExists ? filmListsPageCount.value : filmListsPageCount.value + 1
  filmListsNumPages.value = Math.max(1, Math.ceil(filmListsPageCount.value / filmListsPageSize.value))
  filmListsPageLoaded.value = true
  filmListsPageError.value = ''
}

function filterCreditsMembers(members) {
  if (!creditsFilterTerm.value) return members

  return members.filter((member) => {
    const name = String(member.name || '').toLowerCase()
    const role = String(member.role || '').toLowerCase()
    return name.includes(creditsFilterTerm.value) || role.includes(creditsFilterTerm.value)
  })
}

function normalizeStringList(items) {
  if (!Array.isArray(items)) return []

  return items.map((item) => String(item || '').trim()).filter(Boolean)
}

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`
}

function formatRelativeTime(value, fallback = '') {
  if (!value) return fallback

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return fallback

  const diffMs = date.getTime() - Date.now()
  const absSeconds = Math.round(Math.abs(diffMs) / 1000)
  const absMinutes = Math.round(absSeconds / 60)
  const absHours = Math.round(absMinutes / 60)
  const absDays = Math.round(absHours / 24)
  const absMonths = Math.round(absDays / 30)
  const absYears = Math.round(absDays / 365)

  const rtf = typeof Intl !== 'undefined' && Intl.RelativeTimeFormat
    ? new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
    : null

  if (absSeconds < 60) return rtf ? rtf.format(Math.round(diffMs / 1000), 'second') : fallback
  if (absMinutes < 60) return rtf ? rtf.format(Math.round(diffMs / 60000), 'minute') : fallback
  if (absHours < 24) return rtf ? rtf.format(Math.round(diffMs / 3600000), 'hour') : fallback
  if (absDays < 30) return rtf ? rtf.format(Math.round(diffMs / 86400000), 'day') : fallback
  if (absMonths < 12) return rtf ? rtf.format(Math.round(diffMs / 2592000000), 'month') : fallback
  return rtf ? rtf.format(Math.round(diffMs / 31536000000), 'year') : fallback
}

function normalizeOverviewComment(comment) {
  return {
    id: comment.id,
    parentId: comment.parent_id ?? null,
    level: Number(comment.level ?? 0),
    comment: String(comment.comment || ''),
    containsSpoilers: Boolean(comment.contains_spoilers),
    submitDate: comment.submit_date || null,
    submitDateText: comment.submit_date_text || '',
    username: String(comment.username || ''),
    userHref: buildFilmsomniacPath(comment.username),
  }
}

const OVERVIEW_COMMENT_PREVIEW_LIMIT = 500

function clipOverviewCommentPreview(commentText) {
  const previewText = String(commentText || '')
  if (previewText.length <= OVERVIEW_COMMENT_PREVIEW_LIMIT) {
    return previewText
  }

  const clippedText = previewText.slice(0, OVERVIEW_COMMENT_PREVIEW_LIMIT).trimEnd()
  const lastSpaceIndex = clippedText.lastIndexOf(' ')
  const clippedToWord = lastSpaceIndex > 0 ? clippedText.slice(0, lastSpaceIndex) : clippedText
  return `${clippedToWord} ...`
}

function overviewCommentPreviewText(comment) {
  if (comment.containsSpoilers) {
    return 'This comment contains spoilers.'
  }
  return clipOverviewCommentPreview(comment.comment)
}

function overviewCommentIsTruncated(comment) {
  return !comment.containsSpoilers && comment.comment.length > OVERVIEW_COMMENT_PREVIEW_LIMIT
}

function overviewCommentTimeText(comment) {
  return formatRelativeTime(comment.submitDate, comment.submitDateText)
}

function compareOverviewComments(a, b) {
  const aTime = Date.parse(a.submitDate || '') || 0
  const bTime = Date.parse(b.submitDate || '') || 0
  return bTime - aTime
}

function pillParts(items) {
  return items.flatMap((item, index) =>
    index === 0 ? [['pill', item]] : [['text', ' '], ['pill', item]],
  )
}

function rowsOfTwo(specs) {
  const rows = []
  for (let index = 0; index < specs.length; index += 2) {
    rows.push(specs.slice(index, index + 2))
  }
  return rows
}

function buildAuthHeaders() {
  const headers = {}
  if (auth.state.access) {
    headers.Authorization = `Bearer ${auth.state.access}`
  }
  return headers
}

function resetFilmListModalForm() {
  filmListModalError.value = ''
  filmListCreateMode.value = false
  filmListSelectedListId.value = ''
  filmListPickerOpen.value = false
  filmListCreateTitle.value = ''
  filmListCreateDescription.value = ''
  filmListCreatePublic.value = true
}

let filmListSuccessDismissTimer = null

function clearFilmListSuccessNotice(force = false) {
  if (filmListSuccessDismissTimer) {
    clearTimeout(filmListSuccessDismissTimer)
    filmListSuccessDismissTimer = null
  }
  filmListSuccessDismissing.value = false
  if (force) {
    filmListSuccessTitle.value = ''
  }
}

function showFilmListSuccessNotice(title) {
  clearFilmListSuccessNotice(true)
  filmListSuccessTitle.value = String(title || '').trim()
}

function dismissFilmListSuccessNotice() {
  if (!filmListSuccessTitle.value || filmListSuccessDismissing.value) {
    return
  }
  filmListSuccessDismissing.value = true
  filmListSuccessDismissTimer = setTimeout(() => {
    filmListSuccessTitle.value = ''
    filmListSuccessDismissing.value = false
    filmListSuccessDismissTimer = null
  }, 240)
}

function closeFilmListModal(force = false) {
  if (filmListModalPending.value && !force) {
    return
  }
  filmListModalOpen.value = false
  filmListModalLoading.value = false
  resetFilmListModalForm()
}

function openFilmListCreateMode() {
  filmListModalError.value = ''
  filmListPickerOpen.value = false
  filmListCreateMode.value = true
}

function selectFilmList(listId) {
  filmListSelectedListId.value = String(listId || '')
  filmListPickerOpen.value = false
}

async function loadOwnedFilmLists(currentSlug) {
  filmListOptions.value = []
  filmListSelectedListId.value = ''
  filmListModalLoading.value = true
  filmListModalError.value = ''

  try {
    const response = await fetch(`/api/v1/films/${currentSlug}/lists`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closeFilmListModal(true)
      return
    }
    if (!response.ok) {
      filmListModalError.value = 'Unable to load your lists right now.'
      return
    }
    const data = await response.json()
    filmListOptions.value = Array.isArray(data.results) ? data.results : []
  } catch {
    filmListModalError.value = 'Unable to load your lists right now.'
  } finally {
    filmListModalLoading.value = false
  }
}

async function openFilmListModal() {
  if (!slug.value || !isAuthenticated.value || filmListModalPending.value) {
    return
  }

  filmListModalOpen.value = true
  resetFilmListModalForm()
  await loadOwnedFilmLists(slug.value)
}

function applyFilmListsPagePayload(data, fallbackPageNumber = filmListsPageNumber.value) {
  if (!data || typeof data !== 'object') {
    filmListsPageEntries.value = []
    filmListsPageCount.value = 0
    filmListsPageNumber.value = fallbackPageNumber
    filmListsPageSize.value = 10
    filmListsNumPages.value = 1
    filmListsPageLoaded.value = true
    return
  }

  filmListsPageNumber.value = Number.parseInt(data.page, 10) || fallbackPageNumber
  filmListsPageSize.value = Number.parseInt(data.page_size, 10) || 10
  filmListsPageCount.value = Number.parseInt(data.count, 10) || 0
  filmListsNumPages.value = Math.max(1, Number.parseInt(data.num_pages, 10) || 1)
  filmListsPageEntries.value = Array.isArray(data.results)
    ? data.results.map(normalizeFilmListsPageEntry).filter(Boolean)
    : []
  filmListsPageLoaded.value = true
  filmListsPageError.value = ''
}

async function loadFilmListsPage(
  pageNumber = filmListsPageNumber.value,
  currentSlug = slug.value,
  section = activeSection.value,
  includeAuth = isAuthenticated.value,
) {
  filmListsPageEntries.value = []
  filmListsPageError.value = ''
  filmListsPageLoading.value = true
  filmListsPageLoaded.value = false

  if (!currentSlug || section !== 'lists' || typeof fetch !== 'function') {
    filmListsPageLoading.value = false
    return
  }

  const normalizedPageNumber = Math.max(1, Number.parseInt(pageNumber, 10) || 1)
  const headers = includeAuth ? buildAuthHeaders() : {}
  const requestOptions = Object.keys(headers).length > 0 ? { headers } : undefined

  try {
    const response = await fetch(
      `/api/v1/films/${currentSlug}/lists/page?page=${normalizedPageNumber}`,
      requestOptions,
    )
    if (response.status === 401 || response.status === 403) {
      if (includeAuth) {
        auth.clearTokens()
        await loadFilmListsPage(normalizedPageNumber, currentSlug, section, false)
      }
      return
    }
    if (!response.ok) {
      filmListsPageError.value = 'Unable to load lists right now.'
      return
    }
    const data = await response.json()
    applyFilmListsPagePayload(data, normalizedPageNumber)
  } catch {
    filmListsPageError.value = 'Unable to load lists right now.'
  } finally {
    filmListsPageLoading.value = false
  }
}

function goToFilmListsPage(pageNumber) {
  const normalizedPageNumber = Number.parseInt(pageNumber, 10) || 1
  if (
    filmListsPageLoading.value ||
    normalizedPageNumber < 1 ||
    normalizedPageNumber > filmListsNumPages.value
  ) {
    return
  }
  loadFilmListsPage(normalizedPageNumber)
}

async function submitFilmListAddition() {
  if (
    filmListModalPending.value ||
    !slug.value ||
    !isAuthenticated.value ||
    !filmListSelectedListId.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  filmListModalPending.value = true
  filmListModalError.value = ''
  try {
    const response = await fetch(`/api/v1/films/${slug.value}/lists`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ list_id: Number.parseInt(filmListSelectedListId.value, 10) }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closeFilmListModal(true)
      return
    }
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      filmListModalError.value = data.list_id || 'Unable to add this film to the list right now.'
      return
    }
    showFilmListSuccessNotice(data.list?.title)
    prependOverviewList(data.overview_list)
    prependFilmListsPageEntry(data.overview_list)
    closeFilmListModal(true)
  } catch {
    filmListModalError.value = 'Unable to add this film to the list right now.'
  } finally {
    filmListModalPending.value = false
  }
}

async function submitFilmListCreate() {
  if (
    filmListModalPending.value ||
    !slug.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  filmListModalPending.value = true
  filmListModalError.value = ''
  try {
    const response = await fetch(`/api/v1/films/${slug.value}/lists/create`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: filmListCreateTitle.value,
        description: filmListCreateDescription.value,
        public: filmListCreatePublic.value,
      }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closeFilmListModal(true)
      return
    }
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      filmListModalError.value = data.title || 'Unable to create the list right now.'
      return
    }
    showFilmListSuccessNotice(data.list?.title)
    prependOverviewList(data.overview_list)
    prependFilmListsPageEntry(data.overview_list)
    closeFilmListModal(true)
  } catch {
    filmListModalError.value = 'Unable to create the list right now.'
  } finally {
    filmListModalPending.value = false
  }
}

function buildFilmWatchValue() {
  if (!filmWatchYear.value) {
    return ''
  }
  if (!filmWatchMonth.value) {
    return `${filmWatchYear.value}-00-00`
  }
  if (!filmWatchDay.value) {
    return `${filmWatchYear.value}-${filmWatchMonth.value}-00`
  }
  return `${filmWatchYear.value}-${filmWatchMonth.value}-${filmWatchDay.value}`
}

function validateFilmWatchDateSelection() {
  if (filmWatchDay.value && !filmWatchYear.value && !filmWatchMonth.value) {
    return 'You have to choose year and month'
  }
  if (filmWatchDay.value && filmWatchYear.value && !filmWatchMonth.value) {
    return 'You have to choose month'
  }
  if (!filmWatchYear.value && (filmWatchMonth.value || filmWatchDay.value)) {
    return 'You have to choose year'
  }
  return ''
}

function clearFilmWatchForm() {
  filmWatchDatePickerOpen.value = ''
  filmWatchYear.value = ''
  filmWatchMonth.value = ''
  filmWatchDay.value = ''
  filmWatchFormat.value = ''
  filmWatchDescription.value = ''
  filmWatchPublicDescription.value = false
  filmWatchModalError.value = ''
}

function setFilmWatchFormFromWatch(watch) {
  clearFilmWatchForm()

  const watched = String(watch?.watched || '').trim()
  const watchedMatch = watched.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (watchedMatch) {
    const [, year, month, day] = watchedMatch
    filmWatchYear.value = year
    filmWatchMonth.value = month === '00' ? '' : month
    filmWatchDay.value = day === '00' ? '' : day
  }

  filmWatchFormat.value = watch?.format || ''
  filmWatchDescription.value = watch?.description || ''
  filmWatchPublicDescription.value = Boolean(watch?.publicDescription)
}

function closeFilmWatchModal(force = false) {
  if (filmWatchModalPending.value && !force) {
    return
  }
  filmWatchModalOpen.value = false
  filmWatchDatePickerOpen.value = ''
  filmWatchEditingId.value = null
  filmWatchModalError.value = ''
}

function setFilmWatchToday() {
  const now = new Date()
  filmWatchDatePickerOpen.value = ''
  filmWatchYear.value = String(now.getUTCFullYear())
  filmWatchMonth.value = String(now.getUTCMonth() + 1).padStart(2, '0')
  filmWatchDay.value = String(now.getUTCDate()).padStart(2, '0')
}

function toggleFilmWatchDatePicker(pickerName) {
  filmWatchDatePickerOpen.value = filmWatchDatePickerOpen.value === pickerName ? '' : pickerName
}

function selectFilmWatchYear(year) {
  filmWatchYear.value = year
  filmWatchDatePickerOpen.value = ''
}

function selectFilmWatchMonth(month) {
  filmWatchMonth.value = month
  filmWatchDatePickerOpen.value = ''
}

function selectFilmWatchDay(day) {
  filmWatchDay.value = day
  filmWatchDatePickerOpen.value = ''
}

async function loadFilmWatchStatus(currentSlug) {
  if (!currentSlug || !isAuthenticated.value || typeof fetch !== 'function') {
    filmWatchExistingCount.value = 0
    return
  }

  try {
    const response = await fetch(`/api/v1/films/${currentSlug}/watch`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      filmWatchModalOpen.value = false
      return
    }
    if (!response.ok) return
    const data = await response.json()
    filmWatchExistingCount.value = Number.parseInt(data.count, 10) || 0
  } catch {
    // Leave the modal usable even if the watch status request fails.
  }
}

async function loadFilmWatchedState(currentSlug) {
  if (!currentSlug || !isAuthenticated.value || typeof fetch !== 'function') {
    filmWatched.value = false
    filmWatchExistingCount.value = 0
    filmLatestWatch.value = null
    return
  }

  try {
    const response = await fetch(`/api/v1/films/${currentSlug}/watch`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      filmWatched.value = false
      filmLatestWatch.value = null
      return
    }
    if (!response.ok) return
    const data = await response.json()
    const watchCount = Number.parseInt(data.count, 10) || 0
    filmWatched.value = Boolean(data.has_watched) || watchCount > 0
    filmWatchExistingCount.value = watchCount
    filmLatestWatch.value = normalizeLatestFilmWatch(data.latest)
  } catch {
    // Keep the current watched state if the request fails.
  }
}

async function loadFilmYouWatches(currentSlug = slug.value, section = activeSection.value) {
  if (section !== 'you') {
    return
  }

  filmYouWatchesError.value = ''

  if (!currentSlug || !isAuthenticated.value || typeof fetch !== 'function') {
    filmYouWatches.value = []
    filmYouWatchesLoaded.value = true
    filmYouWatchesLoading.value = false
    return
  }

  filmYouWatchesLoading.value = true
  try {
    const response = await fetch(`/api/v1/films/${currentSlug}/watches`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      filmYouWatches.value = []
      filmYouWatchesLoaded.value = true
      return
    }
    if (!response.ok) {
      filmYouWatchesError.value = 'Unable to load watches right now.'
      return
    }
    const data = await response.json()
    filmYouWatches.value = Array.isArray(data.watches)
      ? data.watches.map(normalizeFilmYouWatch).filter(Boolean).sort(compareFilmYouWatches)
      : []
    filmYouWatchesLoaded.value = true
  } catch {
    filmYouWatchesError.value = 'Unable to load watches right now.'
  } finally {
    filmYouWatchesLoading.value = false
  }
}

async function openFilmWatchModal() {
  if (!slug.value || !isAuthenticated.value || filmWatchModalPending.value) {
    return
  }

  clearFilmWatchForm()
  filmWatchEditingId.value = null
  filmWatchModalOpen.value = true
  await loadFilmWatchStatus(slug.value)
}

function openFilmWatchEditModal(watch) {
  if (!watch || filmWatchModalPending.value) {
    return
  }

  filmWatchEditingId.value = watch.id
  setFilmWatchFormFromWatch(watch)
  filmWatchModalOpen.value = true
}

async function removeFilmYouWatch(watch) {
  if (!watch?.id || !slug.value || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  if (!window.confirm('Remove this watch?')) {
    return
  }

  try {
    const response = await fetch(`/api/v1/films/${slug.value}/watches/${watch.id}`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      filmYouWatchesError.value = 'Unable to remove the watch right now.'
      return
    }

    removeFilmYouWatchFromList(watch.id)
    await loadFilmWatchedState(slug.value)
  } catch {
    filmYouWatchesError.value = 'Unable to remove the watch right now.'
  }
}

async function saveFilmWatch() {
  if (!slug.value || !isAuthenticated.value || filmWatchModalPending.value || typeof fetch !== 'function') {
    return
  }

  filmWatchModalError.value = ''
  const dateValidationError = validateFilmWatchDateSelection()
  if (dateValidationError) {
    filmWatchModalError.value = dateValidationError
    return
  }

  filmWatchModalPending.value = true
  try {
    const editingWatchId = filmWatchEditingId.value
    const response = await fetch(editingWatchId
      ? `/api/v1/films/${slug.value}/watches/${editingWatchId}`
      : `/api/v1/films/${slug.value}/watch`, {
      method: editingWatchId ? 'PATCH' : 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        watched: buildFilmWatchValue(),
        format: filmWatchFormat.value || null,
        description: filmWatchDescription.value,
        public_description: filmWatchPublicDescription.value,
        ...(editingWatchId ? {} : { source: 'F' }),
      }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closeFilmWatchModal(true)
      return
    }
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      filmWatchModalError.value =
        data.description ||
        data.watched ||
        data.format ||
        'Unable to save the watch right now.'
      return
    }
    if (editingWatchId) {
      replaceFilmYouWatch(data)
      await loadFilmWatchedState(slug.value)
      closeFilmWatchModal(true)
      return
    }
    filmWatchExistingCount.value = Number.parseInt(data.count, 10) || (filmWatchExistingCount.value + 1)
    filmWatched.value = Boolean(data.has_watched) || filmWatchExistingCount.value > 0
    filmLatestWatch.value = normalizeLatestFilmWatch(data.latest || data.watch)
    if (activeSection.value === 'you') {
      await loadFilmYouWatches(slug.value, 'you')
    }
    closeFilmWatchModal(true)
  } catch {
    filmWatchModalError.value = 'Unable to save the watch right now.'
  } finally {
    filmWatchModalPending.value = false
  }
}

function normalizeRatingValue(value) {
  const parsed = Number.parseInt(value, 10)
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 10 ? parsed : null
}

function applyFilmViewerState(viewerState) {
  if (!viewerState || typeof viewerState !== 'object') {
    return
  }
  const watchCount = Number.parseInt(viewerState.watch_count, 10) || 0
  filmWatched.value = Boolean(viewerState.has_watched) || watchCount > 0
  filmWatchExistingCount.value = watchCount
  filmLatestWatch.value = normalizeLatestFilmWatch(viewerState.latest_watch)
  filmWatchlisted.value = Boolean(viewerState.watchlisted)
  filmFavourited.value = Boolean(viewerState.favourited)
  filmRating.value = normalizeRatingValue(viewerState.rating)
}

watch(filmWatchYear, (nextYear) => {
  if (!nextYear) {
    filmWatchMonth.value = ''
    filmWatchDay.value = ''
    return
  }
  if (filmWatchDay.value && Number.parseInt(filmWatchDay.value, 10) > filmWatchSelectedDayCount.value) {
    filmWatchDay.value = ''
  }
})

watch(filmWatchMonth, (nextMonth) => {
  if (!nextMonth) {
    filmWatchDay.value = ''
    return
  }
  if (filmWatchDay.value && Number.parseInt(filmWatchDay.value, 10) > filmWatchSelectedDayCount.value) {
    filmWatchDay.value = ''
  }
})

async function loadFilmDetail(currentSlug) {
  const confirmPageView = capturePageView()
  const requestedSection = activeSection.value
  film.value = createFallbackFilmState(currentSlug)
  filmOverviewExpanded.value = false
  filmLoaded.value = false
  filmOverviewLoaded.value = false
  filmNotFound.value = false
  if (!currentSlug || typeof fetch !== 'function') {
    filmLoaded.value = true
    return
  }

  try {
    const authHeaders = buildAuthHeaders()
    const detailUrl = requestedSection === 'credits'
      ? `/api/v1/films/${currentSlug}/?include=credits`
      : requestedSection === 'lists'
        ? `/api/v1/films/${currentSlug}/?include=lists`
        : requestedSection === 'comments'
          ? `/api/v1/films/${currentSlug}/?include=comments`
          : requestedSection === 'you'
            ? `/api/v1/films/${currentSlug}/?include=you`
            : `/api/v1/films/${currentSlug}/`
    const response = Object.keys(authHeaders).length
      ? await fetch(detailUrl, { headers: authHeaders })
      : await fetch(detailUrl)
    if (response.status === 404) {
      filmNotFound.value = true
      return
    }
    if (!response.ok) return
    const data = await response.json()
    if (!showFilmNotFound.value) confirmPageView()
    if (currentSlug !== slug.value || requestedSection !== activeSection.value) {
      if (currentSlug === slug.value && activeSection.value === 'overview' && !filmOverviewLoaded.value) {
        loadFilmDetail(slug.value)
      }
      return
    }
    const hasApiField = (field) => Object.prototype.hasOwnProperty.call(data, field)
    const nextYear = hasApiField('year') ? data.year : film.value.year
    const nextOverview = hasApiField('overview')
      ? data.overview || ''
      : film.value.overview
    const nextReleaseDate = hasApiField('release_date')
      ? data.release_date
      : film.value.releaseDate
    const nextMainCast = hasApiField('main_cast')
      ? normalizeMainCast(data.main_cast)
      : film.value.mainCast
    const nextCreditsCast = hasApiField('credits_cast')
      ? normalizeCreditsCast(data.credits_cast)
      : film.value.creditsCast
    if (hasApiField('credits_cast')) {
      filmCreditsLoaded.value = true
    }
    const nextDirectors = hasApiField('directors')
      ? normalizeDirectors(data.directors)
      : film.value.directors
    const nextCreditsDirectors = hasApiField('directors')
      ? normalizeCreditDirectors(data.directors)
      : film.value.creditsDirectors
    const nextCreditsWriters = hasApiField('writers')
      ? normalizeCreditWriters(data.writers)
      : film.value.creditsWriters
    const nextCreditsCrew = hasApiField('crew')
      ? normalizeCreditCrew(data.crew)
      : film.value.creditsCrew
    const nextRuntimeText = hasApiField('runtime_text')
      ? data.runtime_text || ''
      : film.value.runtimeText
    const nextGenres = hasApiField('genres')
      ? normalizeStringList(data.genres)
      : film.value.genres
    const nextSpokenLanguages = hasApiField('spoken_languages')
      ? normalizeStringList(data.spoken_languages)
      : film.value.spokenLanguages
    const nextProductionCountries = hasApiField('production_countries')
      ? normalizeStringList(data.production_countries)
      : film.value.productionCountries
    const nextImdbUrl = hasApiField('imdb_url')
      ? data.imdb_url || ''
      : film.value.imdbUrl
    const nextPosterUrl = hasApiField('poster_url')
      ? data.poster_url || posterPlaceholderImage
      : film.value.posterUrl || posterPlaceholderImage
    const nextHasBackground = hasApiField('has_background')
      ? data.has_background === true
      : (hasApiField('background_url') ? Boolean(data.background_url) : film.value.hasBackground)
    const nextBackgroundUrl = hasApiField('background_url')
      ? data.background_url || defaultBackgroundImage
      : backgroundUrlFromKnownState(film.value.backgroundUrl, nextHasBackground)
    const nextMobileBackgroundUrl = hasApiField('mobile_background_url')
      ? data.mobile_background_url || ''
      : film.value.mobileBackgroundUrl
    const nextOverviewLists = hasApiField('overview_lists')
      ? (Array.isArray(data.overview_lists)
          ? data.overview_lists.map(normalizeOverviewList).filter(Boolean)
          : [])
      : film.value.overviewLists
    const nextCanAccessBadmin = hasApiField('can_access_badmin')
      ? data.can_access_badmin === true
      : film.value.canAccessBadmin
    const nextShowAds = hasApiField('show_ads')
      ? data.show_ads !== false
      : film.value.showAds
    if (hasApiField('viewer_state')) {
      applyFilmViewerState(data.viewer_state)
    }
    if (hasApiField('lists_page')) {
      applyFilmListsPagePayload(data.lists_page)
    }
    if (hasApiField('you_watches')) {
      applyFilmYouWatchesPayload(data.you_watches)
    }
    if (!['credits', 'lists', 'comments', 'you'].includes(requestedSection)) {
      filmOverviewLoaded.value = true
    }
    film.value = {
      ...film.value,
      id: hasApiField('id') ? data.id : film.value.id,
      title: data.title || film.value.title,
      year: nextYear,
      overview: nextOverview,
      releaseDate: nextReleaseDate,
      mainCast: nextMainCast,
      creditsCast: nextCreditsCast,
      creditsDirectors: nextCreditsDirectors,
      creditsWriters: nextCreditsWriters,
      creditsCrew: nextCreditsCrew,
      directors: nextDirectors,
      runtimeText: nextRuntimeText,
      genres: nextGenres,
      spokenLanguages: nextSpokenLanguages,
      productionCountries: nextProductionCountries,
      imdbUrl: nextImdbUrl,
      canAccessBadmin: nextCanAccessBadmin,
      showAds: nextShowAds,
      posterUrl: nextPosterUrl,
      backgroundUrl: nextBackgroundUrl,
      mobileBackgroundUrl: nextMobileBackgroundUrl,
      hasBackground: nextHasBackground,
      overviewLists: nextOverviewLists,
    }
  } catch {
    // Keep the prototype fallback data visible until the real API data is available.
  } finally {
    filmLoaded.value = true
  }
}

function backgroundUrlFromKnownState(currentBackgroundUrl, hasBackground) {
  if (currentBackgroundUrl) {
    return currentBackgroundUrl
  }
  return hasBackground ? '' : defaultBackgroundImage
}

async function loadLatestOverviewComments(filmId, section = activeSection.value) {
  latestOverviewComments.value = []
  latestOverviewCommentsLoaded.value = false

  if (!filmId || section !== 'overview' || typeof fetch !== 'function') {
    return
  }

  try {
    const response = await fetch(`/api/v1/comments/film/${filmId}/`)
    if (!response.ok) {
      return
    }
    const data = await response.json()
    const results = Array.isArray(data.results) ? data.results.map(normalizeOverviewComment) : []
    latestOverviewComments.value = results
      .filter((comment) => comment.parentId === null && comment.level === 0)
      .sort(compareOverviewComments)
      .slice(0, 2)
    latestOverviewCommentsLoaded.value = true
  } catch {
    // Keep the section quiet if the preview comments request fails.
  }
}

async function loadFilmFavouriteStatus(currentSlug) {
  filmFavourited.value = false

  if (!currentSlug || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  try {
    const response = await fetch(`/api/v1/films/${currentSlug}/favourite`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    filmFavourited.value = Boolean(data.favourited)
  } catch {
    // Keep the control in the default state if the favourite status request fails.
  }
}

async function loadFilmWatchlistStatus(currentSlug) {
  filmWatchlisted.value = false

  if (!currentSlug || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  try {
    const response = await fetch(`/api/v1/films/${currentSlug}/watchlist`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    filmWatchlisted.value = Boolean(data.watchlisted)
  } catch {
    // Keep the control in the default state if the watchlist status request fails.
  }
}

async function loadFilmRatingStatus(currentSlug) {
  filmRating.value = null

  if (!currentSlug || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  try {
    const response = await fetch(`/api/v1/films/${currentSlug}/rating`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    filmRating.value = normalizeRatingValue(data.rating)
  } catch {
    // Keep the control in the default state if the rating status request fails.
  }
}

async function toggleFilmWatchlist() {
  if (filmWatchlistPending.value || !slug.value || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  filmWatchlistPending.value = true
  try {
    const response = await fetch(`/api/v1/films/${slug.value}/watchlist`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'F' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    filmWatchlisted.value = Boolean(data.watchlisted)
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    filmWatchlistPending.value = false
  }
}

async function toggleFilmFavourite() {
  if (filmFavouritePending.value || !slug.value || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  filmFavouritePending.value = true
  try {
    const response = await fetch(`/api/v1/films/${slug.value}/favourite`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'F' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    filmFavourited.value = Boolean(data.favourited)
    if (data.watched === true) {
      filmWatched.value = true
      if (activeSection.value === 'you') {
        await loadFilmYouWatches(slug.value, 'you')
      }
    }
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    filmFavouritePending.value = false
  }
}

async function setFilmRating(ratingValue) {
  if (
    filmRatingPending.value ||
    !slug.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  filmRatingPending.value = true
  try {
    const response = await fetch(`/api/v1/films/${slug.value}/rating`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ rating: ratingValue, source: 'F' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    filmRating.value = normalizeRatingValue(data.rating)
    if (data.watched === true) {
      filmWatched.value = true
      if (activeSection.value === 'you') {
        await loadFilmYouWatches(slug.value, 'you')
      }
    }
    hoverRating.value = 0
  } catch {
    // Leave the current rating unchanged on network or API failure.
  } finally {
    filmRatingPending.value = false
  }
}

async function removeFilmRating() {
  if (
    filmRatingPending.value ||
    !slug.value ||
    !isAuthenticated.value ||
    !isFilmRated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  filmRatingPending.value = true
  try {
    const response = await fetch(`/api/v1/films/${slug.value}/rating`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    filmRating.value = null
    hoverRating.value = 0
  } catch {
    // Leave the current rating unchanged on network or API failure.
  } finally {
    filmRatingPending.value = false
  }
}

watch(slug, loadFilmDetail, { immediate: true })
watch(documentTitle, setDocumentTitle, { immediate: true })
watch(() => film.value.posterUrl, (src) => {
  resetFilmPosterLoading(src)
}, { immediate: true })
watch(slug, () => {
  filmOverviewExpanded.value = false
  filmListOptions.value = []
  clearFilmListSuccessNotice(true)
  closeFilmListModal(true)
  filmListsPageLoading.value = false
  filmListsPageLoaded.value = false
  filmListsPageError.value = ''
  filmListsPageNumber.value = 1
  filmListsPageSize.value = 10
  filmListsPageCount.value = 0
  filmListsNumPages.value = 1
  filmListsPageEntries.value = []
  filmYouWatches.value = []
  filmYouWatchesLoading.value = false
  filmYouWatchesLoaded.value = false
  filmYouWatchesError.value = ''
  filmCreditsLoaded.value = false
  filmOverviewLoaded.value = false
})
watch([() => film.value.id, activeSection], ([filmId, section]) => {
  loadLatestOverviewComments(filmId, section)
}, { immediate: true })
watch(activeSection, (section) => {
  if (section === 'overview' && filmLoaded.value && !filmOverviewLoaded.value) {
    loadFilmDetail(slug.value)
  }
  if (section === 'credits' && filmLoaded.value && !filmCreditsLoaded.value) {
    loadFilmDetail(slug.value)
  }
  if (section === 'lists' && filmLoaded.value && !filmListsPageLoaded.value) {
    loadFilmListsPage(filmListsPageNumber.value)
  }
  if (section === 'you' && filmLoaded.value && !filmYouWatchesLoaded.value) {
    loadFilmYouWatches(slug.value, section)
  }
}, { immediate: true })
watch([slug, isAuthenticated], ([currentSlug, authenticated]) => {
  if (!authenticated) {
    filmListModalPending.value = false
    filmListModalLoading.value = false
    filmListOptions.value = []
    closeFilmListModal(true)
    filmWatchlisted.value = false
    filmWatchlistPending.value = false
    filmFavourited.value = false
    filmFavouritePending.value = false
    filmRating.value = null
    filmRatingPending.value = false
    filmWatched.value = false
    filmWatchExistingCount.value = 0
    filmLatestWatch.value = null
    filmYouWatches.value = []
    filmYouWatchesLoading.value = false
    filmYouWatchesLoaded.value = activeSection.value === 'you'
    filmYouWatchesError.value = ''
    filmWatchModalPending.value = false
    closeFilmWatchModal(true)
    if (activeSection.value === 'lists' && filmListsPageLoaded.value) {
      loadFilmListsPage(filmListsPageNumber.value, currentSlug, 'lists', false)
    }
    return
  }
  if (activeSection.value === 'you' && filmLoaded.value && !filmYouWatchesLoaded.value) {
    loadFilmYouWatches(currentSlug, 'you')
  }
  if (activeSection.value === 'lists' && filmListsPageLoaded.value) {
    loadFilmListsPage(filmListsPageNumber.value, currentSlug, 'lists', true)
  }
}, { immediate: true })

function previewRating(star) {
  hoverRating.value = star
}

function resetRatingPreview() {
  hoverRating.value = 0
}

function ratingStarClass(star) {
  const visibleRating = hoverRating.value || filmRating.value || 0
  return [
    'rating-star',
    star <= visibleRating ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function scrollCreditsToAnchor(event) {
  const href = event?.currentTarget?.getAttribute('href') || ''
  const targetId = href.startsWith('#') ? href.slice(1) : ''
  if (!targetId) return

  const pageRoot = event?.currentTarget?.closest('.single-film-prototype')
  const target = pageRoot?.querySelector(`#${targetId}`) || document.getElementById(targetId)
  target?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  })
}

function toggleFilmOverview() {
  if (!hasExpandableFilmOverview.value) return
  filmOverviewExpanded.value = !filmOverviewExpanded.value
}

const CastMember = defineComponent({
  props: {
    image: {
      type: String,
      required: true,
    },
    actor: {
      type: String,
      required: true,
    },
    role: {
      type: String,
      default: '',
    },
    url: {
      type: String,
      default: '#',
    },
  },
  setup(props) {
    return () => {
      const castNameChildren = [h('a', { href: props.url || '#' }, props.actor)]
      if ((props.role || '').trim()) {
        castNameChildren.push(h('p', props.role))
      }

      return h('div', { class: 'cast-member', 'data-testid': 'cast-member' }, [
        h('div', { class: 'cast-image' }, [
          h('a', { href: props.url || '#' }, [h('img', { src: props.image, alt: '' })]),
        ]),
        h('div', { class: 'cast-name' }, castNameChildren),
      ])
    }
  },
})

const specRows = computed(() => {
  const firstRow = []
  const remainingSpecs = []

  if (directors.value.length > 0) {
    firstRow.push({
      class: 'directed-by',
      testId: 'spec-directed-by',
      title: 'Directed by:',
      body: [['directors', directors.value]],
    })
  }
  if (releaseDateDisplay.value) {
    firstRow.push({
      class: 'released',
      testId: 'spec-released',
      title: 'Released:',
      titleClass: 'pill-title',
      body: [['release-date', releaseDateDisplay.value]],
    })
  }

  if (runtimeText.value) {
    remainingSpecs.push({
      title: 'Runtime:',
      body: [['p', runtimeText.value]],
    })
  }
  if (genres.value.length > 0) {
    remainingSpecs.push({
      title: 'Genres:',
      titleClass: 'pill-title',
      body: pillParts(genres.value),
    })
  }
  if (spokenLanguages.value.length > 0) {
    remainingSpecs.push({
      title: 'Languages:',
      body: pillParts(spokenLanguages.value),
    })
  }
  if (productionCountries.value.length > 0) {
    remainingSpecs.push({
      title: 'Countries:',
      body: pillParts(productionCountries.value),
    })
  }
  if (relatedResourceParts.value.length > 0) {
    remainingSpecs.push({
      title: 'Related resources:',
      body: relatedResourceParts.value,
    })
  }

  return [
    ...(firstRow.length > 0 ? [firstRow] : []),
    ...rowsOfTwo(remainingSpecs),
  ]
})

const mobileSpecRows = computed(() => {
  const firstRow = []
  const remainingSpecs = []

  const directedBySpec = directors.value.length > 0
    ? {
      class: 'directed-by',
      testId: 'spec-directed-by',
      title: 'Directed by:',
      body: [['directors', directors.value]],
    }
    : null

  const releasedSpec = releaseDateDisplay.value
    ? {
      class: 'released',
      testId: 'spec-released',
      title: 'Released:',
      titleClass: 'pill-title',
      body: [['release-date', releaseDateDisplay.value]],
    }
    : null

  if (directedBySpec) {
    firstRow.push(directedBySpec)
  } else if (releasedSpec) {
    firstRow.push(releasedSpec)
  }

  if (directedBySpec && releasedSpec) {
    remainingSpecs.push(releasedSpec)
  }

  if (runtimeText.value) {
    remainingSpecs.push({
      title: 'Runtime:',
      body: [['p', runtimeText.value]],
    })
  }
  if (genres.value.length > 0) {
    remainingSpecs.push({
      title: 'Genres:',
      titleClass: 'pill-title',
      body: pillParts(genres.value),
    })
  }
  if (spokenLanguages.value.length > 0) {
    remainingSpecs.push({
      title: 'Languages:',
      body: pillParts(spokenLanguages.value),
    })
  }
  if (productionCountries.value.length > 0) {
    remainingSpecs.push({
      title: 'Countries:',
      body: pillParts(productionCountries.value),
    })
  }
  if (relatedResourceParts.value.length > 0) {
    remainingSpecs.push({
      title: 'Related resources:',
      body: relatedResourceParts.value,
    })
  }

  return [
    ...(firstRow.length > 0 ? [firstRow] : []),
    ...rowsOfTwo(remainingSpecs),
  ]
})

function renderSpecBody(parts, wrapRelated = false) {
  const children = parts.map((part, index) => {
    const [type, value] = part
    if (type === 'text') return value
    if (type === 'a') return h('a', { key: index, class: 'spec-name', href: '#' }, value)
    if (type === 'pill') return h('span', { key: index, class: 'pill' }, value)
    if (type === 'p') return h('p', { key: index, class: 'spec-text' }, value)
    if (type === 'release-date') {
      return h('p', { key: index, class: 'spec-text' }, [
        value.prefix,
        value.year,
      ])
    }
    if (type === 'directors') {
      return value.flatMap((director, directorIndex) => {
        const link = h(
          'a',
          { key: `director-${directorIndex}`, class: 'spec-name', href: director.url },
          director.name,
        )
        if (directorIndex === 0) return [link]
        return [' • ', link]
      })
    }
    if (type === 'resource') {
      const resource = typeof value === 'string' ? { label: value, url: '#' } : value
      const attrs = { class: 'related-link', href: resource.url || '#' }
      if (resource.testId) attrs['data-testid'] = resource.testId
      const link = h('a', attrs, resource.label)
      return wrapRelated
        ? h('p', { key: index, class: 'related-link-p' }, [link])
        : h('p', { key: index }, [link])
    }
    return value
  })
  return children
}

function renderSpec(spec, specIndex, wrapRelated) {
  const attrs = {
    class: ['spec', spec.class, spec.empty ? 'spec--empty' : null],
    key: spec.title || spec.class || specIndex,
  }
  if (spec.testId) {
    attrs['data-testid'] = spec.testId
  }
  if (spec.empty) {
    attrs['aria-hidden'] = 'true'
    return h('div', attrs)
  }

  return h('div', attrs, [
    h('p', { class: ['spec-title', spec.titleClass] }, spec.title),
    ...renderSpecBody(spec.body, wrapRelated),
  ])
}

const FilmSpecs = defineComponent({
  setup() {
    return () =>
      mobileSpecRows.value.map((row, rowIndex) =>
        h(
          'div',
          { class: 'row g-0', key: rowIndex, 'data-testid': 'spec-row' },
          row.map((spec, specIndex) =>
            h('div', { class: 'col g-0', key: spec.title || spec.class || specIndex }, [
              renderSpec(spec, specIndex, true),
            ]),
          ),
        ),
      )
  },
})

const FilmSpecsDesktop = defineComponent({
  setup() {
    return () =>
      specRows.value.flatMap((row) =>
        row.map((spec, specIndex) =>
          renderSpec(spec, specIndex, false),
        ),
      )
  },
})

useMetaDescription(() => buildMetaDescription(
  'film',
  hasLoadedFilmPayload.value ? { title: filmTitle.value, year: releaseYear.value } : {},
  activeSection.value,
))
</script>
